import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux';
import {
    ReactFlow,
    addEdge,
    Background,
    useReactFlow,
    MarkerType,
    applyNodeChanges,
    applyEdgeChanges,
    reconnectEdge,
    ConnectionMode,
    useNodesData,
    useHandleConnections,
    useConnection,
    BackgroundVariant,
    useOnSelectionChange,
    getIncomers,
    getOutgoers,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { v4 as uuidv4 } from 'uuid';
import { Backdrop, Box, Button, Card, Chip, CircularProgress, Drawer, IconButton, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import RootNode from './nodecomponents/RootNode';
import SelectResourceDialog from './SelectResourceDialog';
import ConditionNode from './nodecomponents/ConditionNode';
import PrivacyNode from './nodecomponents/PrivacyNode';
import ConjunctionNode from './nodecomponents/ConjunctionNode';
import SelectionNode from './nodecomponents/SelectionNode';
import { LoadingButton } from '@mui/lab';
import { clearSelectedNodeRedux, getSelectedNodeRedux, setSelectedNodeRedux } from '../../../../redux/selectedNodeSlice';
import SubjectNode from './nodecomponents/SubjectNode';
import RegulatoryRightSide from './RegulatoryRightSide';
import dagre from 'dagre';
import DialogReuse from '../../ReuseComponents/DialogReuse';
import AttributeSelectionNew from './AttributeSelectionNew';
import NewCreateConditionDialog from './NewCreateConditionDialog';
import handleClickFindTargetPath from '../../../../utils/handleClickFindTargetPath';
import findEdgesForPaths from '../../../../utils/findEdgesForPaths';
import hasFlowChanged from '../../../../utils/hasFlowChanged';
import { createRegulatoryDomainDiagram, toRegulatoryDomainFile } from '../../../../utils/regulatoryDiagramDomainState';
import ConfirmDialogReuse from '../../ReuseComponents/ConfirmDialogReuse';
import { setSnackbarMessage } from '../../../../redux/snackbarMessageSlice';
import useUnsavedChangesWarning from '../../../../utils/useUnsavedChangesWarning';
import findUsedAttributes from '../../../../utils/findUsedAttributes';
import findUsedSubjectAttributesInDRG from '../../../../utils/findUsedSubjectAttributesInDRG';
import checkDiagramError from '../../../../utils/checkDiagramError';
import { getSubjectSchema } from '../../../../redux/subjectSchemaSlice';
import { gzip } from 'pako';
import { getAllUpstreamNodes } from '../../../../utils/getAllUpstreamNodes';
import { dataTypes } from '../../../../utils/configDataTypes';
import { createValidator } from '../../../../utils/postgresqlTypeValidator';
import checkUpdateSubjectSchema from '../../../../utils/checkUpdateSubjectSchema';
import { createLDAPValidator } from '../../../../utils/ldapTypeValidator';
import findSubjectAttrToSimulator from '../../../../utils/findSubjectAttrToSimulator';
import sortedProfilesAttributes from '../../../../utils/sortedProfilesAttributes';
import { handleGetSimulatorProfiles } from '../../../../utils/handleGetSimulatorProfiles';
import { getIAAM, getProfile } from '../../../../redux/iaamSlice';
import { getOpenDrawerRight, getOpenPreviewDRG, getOpenSimulatorDRG, setOpenDrawerRight, setOpenPreviewDRG, setOpenSimulatorDRG } from '../../../../redux/layoutControlSlice';
import { getClickNextFile, getCurrentDRG, getHasDiagramChange, getHasDRGFile, setCurrentDRG, setDrgValidation, setHasDiagramChange, setHasDRGFile } from '../../../../redux/drgMgtSlice';
import { getSelectedStatement, getStatementHighlightPaths, setSelectedStatement } from '../../../../redux/simulateMgtSlice';
import { DIAGRAM_TOOLBAR_SAVE_EVENT, DIAGRAM_TYPES } from '../../../../constants/diagramToolbarEvents';

const nodeWidth = 70;
const nodeHeight = 100;

const defaultEdgeOptions = {
    interactionWidth: 60,
};

const defaultEdgeColor = 'gray';

const getNodeBounds = (node) => {
    const position = node?.positionAbsolute || node?.position || { x: 0, y: 0 };
    const width = node?.measured?.width || node?.width || 150;
    const height = node?.measured?.height || node?.height || 40;

    return {
        x: position.x,
        y: position.y,
        width,
        height,
    };
};

const getNodeCenterPoint = (node) => {
    const { x, y, width, height } = getNodeBounds(node);
    return {
        x: x + width / 2,
        y: y + height / 2,
    };
};

const getHandlePoint = (node, handleId) => {
    const { x, y, width, height } = getNodeBounds(node);

    if (handleId?.startsWith('top')) {
        return { x: x + width / 2, y };
    }

    if (handleId?.startsWith('bottom')) {
        return { x: x + width / 2, y: y + height };
    }

    if (handleId?.startsWith('left')) {
        return { x, y: y + height / 2 };
    }

    return { x: x + width, y: y + height / 2 };
};

const getAvailableHandleIds = (node, handleType) => {
    if (!node) return [];

    if (handleType === 'source') {
        if (node.type === 'root') return [];
        if (node.type === 'subject') return ['bottom-source', 'left-source', 'right-source'];

        return ['top-source', 'bottom-source', 'left-source', 'right-source'];
    }

    if (node.type === 'subject') {
        return ['top-target', 'left-target', 'right-target'];
    }

    return ['top-target', 'bottom-target', 'left-target', 'right-target'];
};

const getClosestHandleId = (node, handleType, targetPoint, fallbackHandleId) => {
    const handleIds = getAvailableHandleIds(node, handleType);

    if (handleIds.length === 0) {
        return fallbackHandleId;
    }

    const initialHandleId = fallbackHandleId && handleIds.includes(fallbackHandleId)
        ? fallbackHandleId
        : handleIds[0];

    return handleIds.reduce((closestHandleId, currentHandleId) => {
        const currentPoint = getHandlePoint(node, currentHandleId);
        const closestPoint = getHandlePoint(node, closestHandleId);

        const currentDistance = Math.hypot(currentPoint.x - targetPoint.x, currentPoint.y - targetPoint.y);
        const closestDistance = Math.hypot(closestPoint.x - targetPoint.x, closestPoint.y - targetPoint.y);

        return currentDistance < closestDistance ? currentHandleId : closestHandleId;
    }, initialHandleId);
};

const isSubjectToSubjectConnection = (sourceNode, targetNode) => {
    return sourceNode?.type === 'subject' && targetNode?.type === 'subject';
};

const getOperatorValues = (operator) => {
    if (!operator) return [];

    if (typeof operator === 'string') {
        return [operator];
    }

    if (typeof operator === 'object') {
        return [operator.label, operator.condition, operator.constraint].filter(Boolean);
    }

    return [];
};

const isOperatorSupported = (operators = [], operator) => {
    const selectedOperatorValues = getOperatorValues(operator);

    if (selectedOperatorValues.length === 0) {
        return false;
    }

    return operators.some((item) => {
        const availableOperatorValues = getOperatorValues(item);
        return availableOperatorValues.some((value) => selectedOperatorValues.includes(value));
    });
};

const isInOperator = (operator) => {
    const operatorValues = getOperatorValues(operator);
    const normalizedValues = operatorValues.map(v => String(v).toUpperCase());
    return normalizedValues.includes('IN') || normalizedValues.includes('NOT IN');
};

const isSameNodeForSelection = (nextNode, currentSelectedNode) => {
    if (nextNode === currentSelectedNode) {
        return true;
    }

    if (!nextNode || !currentSelectedNode) {
        return false;
    }

    if (nextNode.id !== currentSelectedNode.id) {
        return false;
    }

    if (nextNode.type !== currentSelectedNode.type) {
        return false;
    }

    if (nextNode.selected !== currentSelectedNode.selected) {
        return false;
    }

    // Rely on immutable updates: reference changes indicate nested content changes.
    if (nextNode.position !== currentSelectedNode.position) {
        return false;
    }

    if (nextNode.measured !== currentSelectedNode.measured) {
        return false;
    }

    if (nextNode.data !== currentSelectedNode.data) {
        return false;
    }

    if (nextNode.width !== currentSelectedNode.width || nextNode.height !== currentSelectedNode.height) {
        return false;
    }

    return true;
};

const getStorageJson = (key, fallback = null) => {
    const raw = localStorage.getItem(key);
    if (raw === null || raw === undefined || raw === '' || raw === 'undefined') {
        return fallback;
    }

    try {
        return JSON.parse(raw);
    } catch {
        return fallback;
    }
};

// const getLayoutedElements = (nodes, edges) => {
//     const dagreGraph = new dagre.graphlib.Graph();
//     dagreGraph.setDefaultEdgeLabel(() => ({}));
//     dagreGraph.setGraph({ rankdir: 'TB' });

//     nodes.forEach((node) => {
//         dagreGraph.setNode(node.id, { width: nodeWidth, height: nodeHeight });
//     });

//     edges.forEach((edge) => {
//         dagreGraph.setEdge(edge.source, edge.target);
//     });

//     dagre.layout(dagreGraph);

//     nodes.forEach((node) => {
//         if (node?.type === 'subject') {
//             const nodeWithPosition = dagreGraph.node(node.id);
//             if (nodeWithPosition) {
//                 node.position = {
//                     x: nodeWithPosition.x - nodeWidth / 2,
//                     y: nodeWithPosition.y - nodeHeight / 2,
//                 };
//             }
//         }
//     });

//     return { nodes, edges };
// };

function RegulatoryDiagram(props) {

    const { nodes,
        setNodes,
        edges,
        setEdges,
        firstCreate,
        setFirstCreate,
        setStatusUpdate,
        handlePreviewDataChange,
        handleDiagramPreviewChange,
        diagramPreview
    } = props;
    const dispatch = useDispatch();
    const openRightSide = useSelector(getOpenDrawerRight);
    const selectedNode = useSelector(getSelectedNodeRedux);
    const [openProperties, setOpenProperties] = useState(false);
    const regulatoryFile = useSelector(getCurrentDRG);
    const [openSelectResource, setOpenSelectResource] = useState(false);
    const [openAttributeSelection, setOpenAttributeSelection] = useState(false);
    const [loading, setLoading] = useState(false);
    const [hoveredEdgeId, setHoveredEdgeId] = useState(null);
    const [dragOverEdgeId, setDragOverEdgeId] = useState(null);

    const [updateProperties, setUpdateProperties] = useState(false);

    const [openResourceUpdate, setOpenResourceUpdate] = useState(false);
    const [resourceUpdateDetail, setResourceUpdateDetail] = useState();

    const [openNotificationDpe, setOpenNotificationDpe] = useState(false);
    const [notificationDpeDetail, setNotificationDpeDetail] = useState();

    const isReconnectRef = useRef(false);

    const [notificationDpeEvent, setNotificationDpeEvent] = useState("");

    const openPreviewDRG = useSelector(getOpenPreviewDRG);
    const [openNotificationBeforePreview, setOpenNotificationBeforePreview] = useState(false);
    const [openNotificationBeforeChangeFile, setOpenNotificationBeforeChangeFile] = useState(false);
    const [loadingConfirmDialog, setLoadingConfirmDialog] = useState(false);

    // const [savedFlow, setSavedFlow] = useState(null);
    // const [isDiagramChanged, setIsDiagramChanged] = useState(false);
    const hasDiagramChange = useSelector(getHasDiagramChange);
    const hasFileChange = useSelector(getHasDRGFile);

    const nodesRef = useRef(nodes);
    const edgesRef = useRef(edges);
    const openPreviewDRGRef = useRef(openPreviewDRG);
    const hasDiagramChangeRef = useRef(hasDiagramChange);
    const handlePreviewRef = useRef(null);
    const handleHighlightRef = useRef(null);
    const handlePreviewDataChangeRef = useRef(handlePreviewDataChange);
    const handleDiagramPreviewChangeRef = useRef(handleDiagramPreviewChange);
    const handleConfirmRef = useRef(null);
    const restoreFlowStateRef = useRef(null);
    const flowChangeTimerRef = useRef(null);
    const flowChangeTokenRef = useRef(0);
    const nodeViewCacheRef = useRef(new Map());
    const regulatoryFileRef = useRef(regulatoryFile);
    const previewCurrentRef = useRef(diagramPreview);
    const dpeRef = useRef(undefined);
    const subjectSchema = useSelector(getSubjectSchema);

    const [lastClickedNodeId, setLastClickedNodeId] = useState(null);
    const [lastClickedEdgeId, setLastClickedEdgeId] = useState(null);
    const [selectedNodeIds, setSelectedNodeIds] = useState([]);
    const [selectedEdgeIds, setSelectedEdgeIds] = useState([]);
    const nextFile = useSelector(getClickNextFile);

    const openSimulatorDRG = useSelector(getOpenSimulatorDRG);
    const openSimulatorDRGRef = useRef(openSimulatorDRG);

    const iaam = useSelector(getIAAM);

    const {
        zoomTo,
        getViewport,
        getNodes,
        getEdges,
        setViewport,
        fitView,
        screenToFlowPosition,
        getNode,
        getEdge,
        addEdges,
        updateEdge,
    } = useReactFlow();

    const overlappedEdgeRef = useRef(null);

    const drawerWidth = 300 + 8;

    // const [profile, setProfile] = useState();
    const [project, setProject] = useState();
    const projectRef = useRef(project);

    const selectedStatement = useSelector(getSelectedStatement);
    const statementHighlightPaths = useSelector(getStatementHighlightPaths);
    const profile = useSelector(getProfile);

    nodesRef.current = nodes;
    edgesRef.current = edges;
    openPreviewDRGRef.current = openPreviewDRG;
    openSimulatorDRGRef.current = openSimulatorDRG;
    hasDiagramChangeRef.current = hasDiagramChange;
    regulatoryFileRef.current = regulatoryFile;
    previewCurrentRef.current = diagramPreview;
    projectRef.current = project;
    handlePreviewDataChangeRef.current = handlePreviewDataChange;
    handleDiagramPreviewChangeRef.current = handleDiagramPreviewChange;

    useEffect(() => {
        const project = getStorageJson('project', null);
        if (project) {
            setProject(project);
        }
    }, []);

    const saveViewportState = useCallback(() => {
        const viewport = getViewport();
        // const all_nodes = getNodes();
        // const all_edges = getEdges();
        const viewport_flow = {
            // nodes: all_nodes,
            // edges: all_edges,
            viewport: viewport
        }
        const allFlows = getStorageJson('regulatory-diagram', {});
        const currentFile = getStorageJson('regulatory-file', {});
        allFlows[currentFile.regulatory_diagram_id] = viewport_flow;
        localStorage.setItem('regulatory-diagram', JSON.stringify(allFlows));
    }, [getViewport]);

    const clearActiveHandles = useCallback(() => {
        setNodes(nodes => nodes.map((node) => ({
            ...node,
            data: {
                ...node.data,
                activeSourceHandleId: null,
                activeTargetHandleId: null,
                showHandle: false
            }
        })));
        setEdges(edges => edges.map(edge => ({
            ...edge,
            reconnectable: true
        })));
    }, [setNodes, setEdges]);

    // ฟังก์ชันตรวจสอบความแตกต่างระหว่างสองอาร์เรย์
    const compareArrays = (oldArray, newArray) => {
        const oldMap = new Map(oldArray.map(item => [item.name, item]));
        const newMap = new Map(newArray.map(item => [item.name, item]));

        const added = [];
        const removed = [];
        const modified = [];

        for (const [name, newItem] of newMap) {
            const oldItem = oldMap.get(name);
            if (!oldItem) {
                added.push(newItem);
            } else if (oldItem.data_type !== newItem.data_type) {
                modified.push({
                    name,
                    oldDataType: oldItem.data_type,
                    newDataType: newItem.data_type
                });
            }
        }

        for (const [name, oldItem] of oldMap) {
            if (!newMap.has(name)) {
                removed.push(oldItem);
            }
        }

        return {
            added,
            removed,
            modified,
            summary: {
                addedCount: added.length,
                removedCount: removed.length,
                modifiedCount: modified.length
            }
        };
    };

    const updateData = async (oldAttr, newAttr) => {
        const result = compareArrays(oldAttr, newAttr);
        setResourceUpdateDetail(result);

        if (result.added.length > 0 || result.removed.length > 0 || result.modified.length > 0) {
            setOpenResourceUpdate(true);
        }
    };

    const handleGetSubjectAttribute = async (nodes) => {
        let resolvedSubjectSchema = Array.isArray(subjectSchema) ? subjectSchema : [];

        if (resolvedSubjectSchema.length === 0) {
            const refreshedSubjectSchema = await checkUpdateSubjectSchema(dispatch, resolvedSubjectSchema, { returnData: true });
            if (Array.isArray(refreshedSubjectSchema)) {
                resolvedSubjectSchema = refreshedSubjectSchema;
            }
        }

        let initial_attributes = nodes.map((node) => {
            if (node.type === 'root') {
                delete node.data.general_properties.subject_attributes; // ลบ key subject_attributes เดิมออกก่อน
                return {
                    ...node,
                    data: {
                        ...node.data,
                        general_properties: {
                            ...node.data.general_properties,
                            subject_schema: resolvedSubjectSchema,
                        }
                    }
                };
            }
            return node;
        });

        initial_attributes = await findUsedSubjectAttributesInDRG(initial_attributes); // อัปเดตสถานะ used ของ attribute

        return initial_attributes;
    };

    const handleUpdateResourceAttribute = async (root_node, node_data) => {
        const accessToken = await iaam.getAccessToken();

        try {
            let option = {
                method: 'GET',
                headers: {
                    'Content-type': 'application/json',
                    'Authorization': `bearer ${accessToken}`
                }
            }
            const currentDpe = dpeRef.current;
            if (currentDpe?.dpe_discovery_url) {
                const resource_schema_endpoint = currentDpe?.resource_schema_endpoint;
                const resource = root_node?.data?.general_properties?.resource;

                const response = await fetch(`${resource_schema_endpoint}?resource=${resource}`, option);
                if (response.ok) {
                    const result = await response.json();
                    if (result.length === 0) { // ไม่มี attribute ใน resource นี้
                        return node_data;
                    } else {
                        const sort_attr = result.sort((a, b) => a.column_name.localeCompare(b.column_name));
                        const new_result = sort_attr.map((attr) => {
                            return {
                                name: attr.column_name,
                                data_type: attr.data_type,
                                checked: false,
                            };
                        });

                        updateData(root_node.data.general_properties.resource_schema, new_result);

                        const isPrimaryKey = (attributes) => {
                            const is_primary_key = attributes.map(attr => {
                                if (attr.is_primary_key) {
                                    return attr.column_name;
                                }
                            }).filter(Boolean);
                            return is_primary_key || [];
                        };

                        let initial_attributes = node_data.map((node) => {
                            if (node.type === 'root') {
                                return {
                                    ...node,
                                    data: {
                                        ...node.data,
                                        general_properties: {
                                            ...node.data.general_properties,
                                            resource_schema: new_result,
                                            primary_key: isPrimaryKey(result)
                                        }
                                    }
                                };
                            }
                            return node;
                        });

                        initial_attributes = findUsedAttributes(initial_attributes); // อัปเดตสถานะ used ของ attribute

                        return initial_attributes;
                    }

                } else {
                    console.error(response);
                    return node_data;
                }
            } else {
                return node_data;
            }

        } catch (error) {
            console.error(error);
            return node_data;
        }

    };

    const validateDPEInProject = (rootNode, latestDpe = dpeRef.current) => {
        if (!latestDpe) {
            return false;
        } else {
            if (latestDpe?.dpe_id === rootNode?.data?.general_properties?.dpe?.dpe_id && latestDpe?.resource_connection?.status === 'OK') {
                return true;
            } else {
                return false;
            }
        }
    };

    const handleValidateDPE = async (node_data) => {
        const root_node = node_data.find(node => node.type === 'root');
        const hasDPE = validateDPEInProject(root_node);

        if (!hasDPE) { // ไม่มี DPE ที่ใช้งานกับ Diagram อยู่ใน Project
            const msg = `No DPE "${root_node?.data?.general_properties?.dpe?.resources_name}" found in this project.`;
            const root_node_update = {
                ...root_node,
                data: {
                    ...root_node.data,
                    error_dpe: true,
                    error_dpe_text: ["The requested DPE data is not available."]
                }
            };
            const nodes_update = node_data.map((node) => {
                if (node.type === 'root') {
                    return root_node_update;
                }
                return node;
            });
            setNotificationDpeDetail(msg);
            setNotificationDpeEvent("dpe_id");
            setOpenNotificationDpe(true);
            return nodes_update;
        } else {
            // ดึงข้อมูล DPE ล่าสุดมาเปรียบเทียบโดยใช้ dpe_id
            const result = await handleUpdateResourceAttribute(root_node, node_data);
            return result;

            // if (hasDPE?.dpe_discovery_url !== root_node?.data?.general_properties?.dpe?.dpe_discovery_url) {  // ตรวจสอบ DPE มีการเปลี่ยนแปลง dpe_discovery_url หรือไม่
            //     const msg = `The DPE host has been changed. Please select the DPE data again.`; // แจ้งให้ user เปลี่ยน genaral properties (Resource)
            //     setNotificationDpeDetail(msg);
            //     setNotificationDpeEvent("dpe_discovery_url");
            //     setOpenNotificationDpe(true);
            //     return node_data;
            // } else {
            //     return handleUpdateResourceAttribute(root_node, node_data);
            // }
        }

    };

    const fetchResources = async (general_properties) => {
        const accessToken = await iaam.getAccessToken();
        let option = {
            method: 'GET',
            headers: {
                'Content-type': 'application/json',
                'Authorization': `bearer ${accessToken}`
            }
        }
        const response = await fetch(`${general_properties?.dpe?.resources_endpoint}`, option);
        if (response.ok) {
            const result = await response.json();
            const findResource = result.find(resource => resource === general_properties?.resource);
            return findResource;
        } else {
            console.error(response);
            return undefined;
        }
    };

    const handleValidateResource = async (node_data) => {
        const root_node = node_data.find(node => node.type === 'root');
        const hasResource = await fetchResources(root_node?.data?.general_properties);
        if (!hasResource) {
            const msg = `The requested resource is not available.`;
            const root_node_update = {
                ...root_node,
                data: {
                    ...root_node.data,
                    error_resource: true,
                    error_resource_text: ["The requested resource is not available."]
                }
            };
            const nodes_update = node_data.map((node) => {
                if (node.type === 'root') {
                    return root_node_update;
                }
                return node;
            });
            setNotificationDpeDetail(msg);
            setNotificationDpeEvent("dpe_id");
            setOpenNotificationDpe(true);
            return nodes_update;
        } else {
            return node_data;
        }
    };

    const restoreFlowState = async (node_data, edge_data) => {
        const allFlows = getStorageJson('regulatory-diagram', {});
        const currentFile = getStorageJson('regulatory-file', {});
        const flowState = allFlows[currentFile.regulatory_diagram_id];
        const validateDPE = await handleValidateDPE(node_data);
        const validateResource = await handleValidateResource(validateDPE);
        const validataSubjectAttribute = await handleGetSubjectAttribute(validateResource);

        const updateEdgesStyle = (edges, nodes) => {
            return edges.map(edge => {
                const sourceNode = nodes.find(node => node.id === edge.source);

                if (sourceNode?.type === 'subject') {
                    // เช็กว่า edge ยังไม่ใช่เส้นประ
                    const isDashed = edge.style?.strokeDasharray === '5,5';
                    if (!isDashed) {
                        return {
                            ...edge,
                            style: {
                                ...edge.style,
                                strokeDasharray: '5,5'
                            }
                        };
                    }
                }

                return edge;
            });
        };

        const handleInitialValidateNode = (nodes, edges) => {
            const updateEdges = edges.map(edge => ({ ...edge, id: `${edge.source}-${edge.target}` }));
            const remakeEdges = updateEdgesStyle(updateEdges, nodes); // ปรับเป็นเส้นประ กรณีที่เป็น diagram เวอร์ชันเก่า
            const persistedDiagram = createRegulatoryDomainDiagram(nodes, remakeEdges);
            handleValidateNode(nodes, remakeEdges);
            setEdges(remakeEdges);
            // setSavedFlow({ nodes: nodes, edges: remakeEdges });
            const updatedRegulatoryFile = {
                ...regulatoryFileRef.current,
                diagram: {
                    ...regulatoryFileRef.current.diagram,
                    nodes: persistedDiagram.nodes,
                    edges: persistedDiagram.edges
                }
            };
            localStorage.setItem('regulatory-file', JSON.stringify(updatedRegulatoryFile));
        };

        setTimeout(() => {
            // const updateEdges = edge_data.map(edge => ({ ...edge, id: `${edge.source}-${edge.target}` }));
            // const remakeEdges = updateEdgesStyle(updateEdges, node_data); // ปรับเป็นเส้นประ กรณีที่เป็น diagram เวอร์ชันเก่า
            // handleValidateNode(validataSubjectAttribute, remakeEdges);
            // setEdges(remakeEdges);
            handleInitialValidateNode(validataSubjectAttribute, edge_data);
        }, 300);
        if (flowState) {
            setTimeout(() => {
                setViewport(flowState.viewport || { x: 0, y: 0, zoom: 1 });
                setLoading(false);
            }, 500);
        } else {
            setTimeout(() => {
                fitView();
                zoomTo(1, { duration: 0 });
                setLoading(false);
            }, 500);
        }
    };

    restoreFlowStateRef.current = restoreFlowState;

    useEffect(() => {
        const abortController = new AbortController();

        const setDPESelcted = async () => {
            const root_node = regulatoryFile?.diagram?.nodes?.find(node => node.type === 'root');
            if (!regulatoryFile?.diagram?.nodes || !root_node) {
                dpeRef.current = undefined;
                return regulatoryFile;
            }

            const rootDpeId = root_node?.data?.general_properties?.dpe?.dpe_id;
            const apiUrl = process.env.REACT_APP_API_URL;

            const updateRegulatoryRootNode = (updater) => {
                return {
                    ...regulatoryFile,
                    diagram: {
                        ...regulatoryFile.diagram,
                        nodes: regulatoryFile.diagram.nodes.map((node) => {
                            if (node.type === 'root') {
                                return updater(node);
                            }
                            return node;
                        })
                    }
                };
            };

            if (!apiUrl || !rootDpeId) {
                dpeRef.current = undefined;
                return regulatoryFile;
            }

            try {
                const response = await fetch(`${apiUrl}/dpe/get-dpe/${rootDpeId}`, {
                    signal: abortController.signal
                });

                if (!response.ok) {
                    throw new Error(`Failed to fetch DPE data: ${response.status}`);
                }

                const dpe_result = await response.json();
                if (!dpe_result?.dpe_discovery_url) {
                    throw new Error('Failed to fetch DPE data: missing dpe_discovery_url');
                }

                const dpe_response = await fetch(dpe_result.dpe_discovery_url, {
                    signal: abortController.signal
                });

                if (!dpe_response.ok) {
                    throw new Error(`Failed to fetch DPE config: ${dpe_response.status}`);
                }

                const dpe_config_result = await dpe_response.json();
                const dpeUpdated = {
                    ...dpe_result,
                    ...dpe_config_result
                };

                dpeRef.current = dpeUpdated;

                return updateRegulatoryRootNode((node) => ({
                    ...node,
                    data: {
                        ...node.data,
                        error_dpe: false,
                        error_dpe_text: [],
                        general_properties: {
                            ...node.data.general_properties,
                            dpe: dpeUpdated
                        }
                    }
                }));
            } catch (error) {
                if (error?.name === 'AbortError') {
                    return regulatoryFile;
                }

                dpeRef.current = undefined;

                return updateRegulatoryRootNode((node) => ({
                    ...node,
                    data: {
                        ...node.data,
                        error_dpe: true,
                        error_dpe_text: ["The requested DPE data is not available."]
                    }
                }));
            }
        }

        const renderDRG = async () => {
            const drg_file = await setDPESelcted();
            if (abortController.signal.aborted) {
                return;
            }
            await restoreFlowStateRef.current?.(
                drg_file?.diagram?.nodes || regulatoryFile?.diagram?.nodes || [],
                drg_file?.diagram?.edges || regulatoryFile?.diagram?.edges || []
            );
        }

        if (regulatoryFile) {
            setLoading(true);
            handlePreviewDataChangeRef.current?.({});
            dispatch(setOpenPreviewDRG(false));
            // dispatch(clearPreviewCurrentRedux());
            handleDiagramPreviewChangeRef.current?.({});
            renderDRG();
        }

        return () => {
            abortController.abort();
        };
    }, [regulatoryFile?.regulatory_diagram_id, regulatoryFile?.updater_at, dispatch]);

    useEffect(() => {
        if (firstCreate && nodes.length > 0) {
            dispatch(setOpenDrawerRight(true));
            setNodes((nds) =>
                nds.map((node, index) => ({ ...node, selected: node.type === 'root' }))
            );
            const root_nodes = nodes.find((node) => node.type === 'root');
            dispatch(setSelectedNodeRedux(root_nodes));
            setOpenProperties(true);
        }
    }, [firstCreate, nodes, dispatch, setNodes])

    const onPaneClick = useCallback((event) => {
        clearActiveHandles();
        event.preventDefault();
        event.stopPropagation();
        if (openPreviewDRGRef.current || openSimulatorDRGRef.current) {
            return;
        }
        setLastClickedNodeId(null);
        setLastClickedEdgeId(null);
        setSelectedNodeIds([]);
        setSelectedEdgeIds([]);
        setNodes((prev) =>
            prev.map((node) => ({ ...node, data: { ...node.data, showHandle: false } }))
        )
        setEdges((prev) =>
            prev.map((edge) => ({ ...edge, selected: false }))
        );
        setFirstCreate(false);
        setOpenProperties(false);
        dispatch(clearSelectedNodeRedux());
    }, [dispatch, setEdges, clearActiveHandles, setFirstCreate, setNodes, openPreviewDRGRef, openSimulatorDRGRef]);

    const onNodeClick = useCallback((event, nodeId) => {

        clearActiveHandles();

        const node = getNode(nodeId);
        console.log("click node: ", node);

        event.stopPropagation(); // ป้องกัน event ซ้อนกัน
        if (!node) return;

        if (openSimulatorDRGRef.current) {
            event.preventDefault();
            dispatch(setSelectedNodeRedux({ ...node, selected: true }));
            setOpenProperties(true);
            dispatch(setOpenDrawerRight(true));
            return;
        }

        dispatch(setSelectedNodeRedux(node));
        setLastClickedNodeId(node.id);
        setLastClickedEdgeId(null);
        setSelectedEdgeIds([]);

        if (openPreviewDRGRef.current) {

            if (node.type === 'subject') {
                if (handlePreviewRef.current) {
                    handlePreviewRef.current(node.id);
                }
            }

        } else {
            if (event.target.classList.contains("react-flow__handle")) {
                setNodes((prev) =>
                    prev.map((nd) => ({
                        ...nd,
                        data: { ...nd.data, showHandle: (nd.id === node.id) ? true : false },
                        selected: nd.id === node.id ? true : false
                    }))
                )
            } else {
                setNodes((prev) =>
                    prev.map((nd) => ({
                        ...nd,
                        data: { ...nd.data, showHandle: (nd.id === node.id) ? true : false },
                        selected: nd.id === node.id ? true : false
                    }))
                )
            }

            setEdges((prev) =>
                prev.map((edge) => ({
                    ...edge,
                    selected: false
                }))
            );

            setFirstCreate(false);
            setOpenProperties(true);

            if (node.type === 'subject') {
                if (handleHighlightRef.current) {
                    handleHighlightRef.current(node.id);
                }
            }
        }

    }, [dispatch, getNode, setEdges, setFirstCreate, setNodes, clearActiveHandles, openPreviewDRGRef, openSimulatorDRGRef]);

    const handleCloseSimulatorFloatingPanel = useCallback(() => {
        setOpenProperties(false);
        dispatch(setOpenDrawerRight(false));
    }, [dispatch]);

    const onEdgeMouseEnter = (event, edge) => {
        if (!openSimulatorDRGRef.current && !openPreviewDRGRef.current) {
            setHoveredEdgeId(edge.id);
        }
    };

    const onEdgeMouseLeave = () => {
        if (!openSimulatorDRGRef.current && !openPreviewDRGRef.current) {
            setHoveredEdgeId(null);
        }
    };

    const edgesWithUpdatedData = useMemo(() => {
        return edges.map((edge) => {
            const isInteractionBlocked = openPreviewDRGRef.current || openSimulatorDRGRef.current;
            const isSelected = edge.selected === true;
            const isHovered = hoveredEdgeId === edge?.id;
            const isTemporaryDragHighlight = dragOverEdgeId === edge?.id;
            const baseStrokeWidth = Number(edge?.style?.strokeWidth) || 1;

            // ตรวจสอบว่า edge ถูกไฮไลต์โดย applyEdgeCondition หรือไม่
            const isHighlightedByCondition = edge?.style?.stroke === '#1976d2';
            // ตรวจสอบว่า edge ถูกไฮไลต์โดยการ interact หรือไม่
            const isHighlightedByInteraction = isTemporaryDragHighlight || isSelected || isHovered;

            const strokeColor = (isHighlightedByCondition || isHighlightedByInteraction) ? '#1976d2' : defaultEdgeColor;

            const strokeWidth = openPreviewDRGRef.current
                ? (isSelected ? Math.max(baseStrokeWidth, 1) : baseStrokeWidth)
                : isTemporaryDragHighlight
                    ? 1.5
                    : (isHovered || isSelected)
                        ? 1
                        : baseStrokeWidth;

            return {
                ...edge,
                reconnectable: !isInteractionBlocked,
                selectable: !isInteractionBlocked,
                deletable: !isInteractionBlocked,
                style: {
                    ...edge.style,
                    stroke: strokeColor,
                    strokeWidth,
                    transition: 'stroke 120ms ease, stroke-width 120ms ease',
                    ...(edge?.style?.strokeDasharray ? { strokeDasharray: '5,5' } : {})
                },
                markerEnd: {
                    ...edge.markerEnd,
                    color: strokeColor,
                },
            };
        });
    }, [edges, hoveredEdgeId, dragOverEdgeId]);

    const getDroppableEdgeIdFromPoint = useCallback((clientX, clientY) => {
        const edgeFound = document
            .elementsFromPoint(clientX, clientY)
            .find((el) => el.classList.contains('react-flow__edge-interaction'))?.parentElement;

        const edgeId = edgeFound?.dataset.id || null;
        const hoveredEdge = edgeId ? getEdge(edgeId) : null;
        const sourceNode = hoveredEdge ? getNode(hoveredEdge.source) : null;
        const targetNode = hoveredEdge ? getNode(hoveredEdge.target) : null;

        return isSubjectToSubjectConnection(sourceNode, targetNode) ? null : edgeId;
    }, [getEdge, getNode]);

    const hasReactFlowDragData = useCallback((event) => {
        return Array.from(event?.dataTransfer?.types || []).includes('application/reactflow');
    }, []);

    const onDragOver = useCallback((event) => {
        if (openPreviewDRGRef.current || openSimulatorDRGRef.current) {
            if (overlappedEdgeRef.current !== null) {
                overlappedEdgeRef.current = null;
                setDragOverEdgeId(null);
            }
            return;
        }

        event.preventDefault();

        const isReactFlowDrag = hasReactFlowDragData(event);
        if (!isReactFlowDrag) {
            if (overlappedEdgeRef.current !== null) {
                overlappedEdgeRef.current = null;
                setDragOverEdgeId(null);
            }
            return;
        }

        event.dataTransfer.dropEffect = 'move';

        const nextEdgeId = getDroppableEdgeIdFromPoint(event.clientX, event.clientY);
        if (overlappedEdgeRef.current !== nextEdgeId) {
            overlappedEdgeRef.current = nextEdgeId;
            setDragOverEdgeId(nextEdgeId);
        }
    }, [getDroppableEdgeIdFromPoint, hasReactFlowDragData]);

    const onDrop = useCallback(
        async (event) => {
            if (openPreviewDRGRef.current || openSimulatorDRGRef.current) {
                event.preventDefault();
                setDragOverEdgeId(null);
                overlappedEdgeRef.current = null;
                return;
            }

            event.preventDefault();

            const type = event.dataTransfer.getData('application/reactflow');
            const edgeId = dragOverEdgeId || getDroppableEdgeIdFromPoint(event.clientX, event.clientY);
            setDragOverEdgeId(null);
            overlappedEdgeRef.current = null;

            // check if the dropped element is valid
            if (typeof type === 'undefined' || !type) {
                return;
            }

            const position = screenToFlowPosition({
                x: event.clientX,
                y: event.clientY,
            });

            const root_node = nodes.find((n) => n.type === 'root');

            let newNode = {
                id: uuidv4(),
                type: type,
                position,
                data: {
                    general_properties: {
                        label: `${type}`,
                        description: "",
                        resource_schema: root_node?.data?.general_properties?.resource_schema
                    },
                    warning: true,
                    warning_text: ["No subject or Authorized Data node connection. Please connect this node to both nodes."],
                    error: false,
                    error_text: [],
                },
            };

            if (type === 'condition') {
                newNode = {
                    ...newNode,
                    data: {
                        ...newNode.data,
                        general_properties: {
                            ...newNode.data.general_properties,
                            conditions: []
                        },
                    }
                };
                // newNode.data.general_properties['conditions'] = [];
            } else if (type === 'privacy') {
                newNode = {
                    ...newNode,
                    data: {
                        ...newNode.data,
                        general_properties: {
                            ...newNode.data.general_properties,
                            privacies: []
                        },
                    }
                };
            } else {
                newNode = { ...newNode };
            }

            if (!newNode?.id) {
                return;
            }

            const appendNodeOnly = () => {
                setNodes((nds) => nds.concat(newNode));
            };

            if (!edgeId) {
                appendNodeOnly();
                return;
            }

            const edge = getEdge(edgeId);
            if (!edge || newNode.id === edge.source || newNode.id === edge.target) {
                appendNodeOnly();
                return;
            }

            const getDefaultEdgeStyle = (sourceType) => (
                sourceType === 'subject'
                    ? { stroke: defaultEdgeColor, strokeWidth: 1, strokeDasharray: '5,5' }
                    : { stroke: defaultEdgeColor, strokeWidth: 1 }
            );

            const sourceNode = getNode(edge.source);
            const targetNode = getNode(edge.target);

            if (!sourceNode || !targetNode || isSubjectToSubjectConnection(sourceNode, targetNode)) {
                appendNodeOnly();
                return;
            }

            const isValidIncomingConnection = shouldConnect(sourceNode, newNode);
            const isValidOutgoingConnection = shouldConnect(newNode, targetNode);

            if (!isValidIncomingConnection || !isValidOutgoingConnection) {
                appendNodeOnly();
                return;
            }

            const hasDuplicateIncomingEdge = edges.some(
                (currentEdge) => currentEdge.id !== edgeId && currentEdge.source === sourceNode.id && currentEdge.target === newNode.id
            );
            const hasDuplicateOutgoingEdge = edges.some(
                (currentEdge) => currentEdge.id !== edgeId && currentEdge.source === newNode.id && currentEdge.target === targetNode.id
            );

            if (hasDuplicateIncomingEdge || hasDuplicateOutgoingEdge) {
                appendNodeOnly();
                return;
            }

            const droppedNodeCenter = getNodeCenterPoint(newNode);
            const sourceNodeCenter = getNodeCenterPoint(sourceNode);
            const targetNodeCenter = getNodeCenterPoint(targetNode);

            const incomingSourceHandle = getClosestHandleId(sourceNode, 'source', droppedNodeCenter, edge.sourceHandle);
            const incomingTargetHandle = getClosestHandleId(newNode, 'target', sourceNodeCenter, edge.targetHandle);
            const outgoingSourceHandle = getClosestHandleId(newNode, 'source', targetNodeCenter, 'bottom-source');
            const outgoingTargetHandle = getClosestHandleId(targetNode, 'target', droppedNodeCenter, edge.targetHandle);

            const incomingEdgeStyle = getDefaultEdgeStyle(sourceNode?.type);
            const outgoingEdgeStyle = getDefaultEdgeStyle(newNode?.type);
            const defaultMarkerEnd = {
                type: MarkerType.ArrowClosed,
                width: 20,
                height: 20,
                color: defaultEdgeColor
            };

            const updatedIncomingEdge = {
                ...edge,
                source: edge.source,
                sourceHandle: incomingSourceHandle,
                target: newNode.id,
                targetHandle: incomingTargetHandle,
                style: incomingEdgeStyle,
                markerEnd: defaultMarkerEnd,
            };

            const newOutgoingEdge = {
                id: `${newNode.id}-${edge.target}`,
                source: newNode.id,
                sourceHandle: outgoingSourceHandle,
                target: edge.target,
                targetHandle: outgoingTargetHandle,
                type: edge.type,
                animated: edge.animated,
                style: outgoingEdgeStyle,
                markerEnd: defaultMarkerEnd,
            };

            const updatedEdges = edges
                .filter((currentEdge) => currentEdge.id !== edgeId)
                .concat(updatedIncomingEdge, newOutgoingEdge);

            const nodesWithNewNode = [...nodes, newNode];
            const find_target_path = handleClickFindTargetPath(targetNode.id, nodesWithNewNode, updatedEdges);

            // filter เอาเฉพาะ path ที่เกี่ยวกับ source และ target เพื่อใช้ในการตรวจสอบ validation ต่างๆ
            const filtered_path = find_target_path.filter(item => {
                const isPath = item.pathNodes.includes(sourceNode.id) && item.pathNodes.includes(targetNode.id);
                const hasNewEdge = item.pathEdges.includes(updatedIncomingEdge.id) && item.pathEdges.includes(newOutgoingEdge.id);
                return isPath && hasNewEdge;
            });

            const valid_duplicate_selection = validateDuplicateSelection(filtered_path, nodesWithNewNode);
            if (!valid_duplicate_selection) {
                dispatch(setSnackbarMessage({
                    open: true,
                    message: "Duplicate selection found.",
                    status: "error",
                }));
                appendNodeOnly();
                return;
            }

            const valid_privacy_attr_duplicate = validateDuplicatePrivacyAttributes(filtered_path, nodesWithNewNode);
            if (!valid_privacy_attr_duplicate) {
                dispatch(setSnackbarMessage({
                    open: true,
                    message: "Duplicate privacy attributes found.",
                    status: "error",
                }));
                appendNodeOnly();
                return;
            }

            const valid_conflict_selection_privacy = validateSelectionAttributesInPath(filtered_path, nodesWithNewNode);
            if (!valid_conflict_selection_privacy) {
                dispatch(setSnackbarMessage({
                    open: true,
                    message: "Conflict between selection and privacy attributes found.",
                    status: "error",
                }));
                appendNodeOnly();
                return;
            }

            setEdges(updatedEdges);

            const subjectNodeId = nodesWithNewNode.find((currentNode) => currentNode.type === 'subject' && currentNode.root === true);
            const authorizeNodeId = nodesWithNewNode.find((currentNode) => currentNode.type === 'root');
            const inherit_node = inheritPrivacyAttributesToSelection(find_target_path, nodesWithNewNode);

            const updatedStatusNodes = (subjectNodeId && authorizeNodeId)
                ? await updateNodeStatus(inherit_node, updatedEdges, subjectNodeId.id, authorizeNodeId.id)
                : inherit_node;

            const updatedNodes = await handleValidateData(updatedStatusNodes, updatedEdges);
            handleValidationFile(updatedNodes, updatedEdges); // อัปเดต validation ของไฟล์ตาม error ของ node
            setNodes(updatedNodes);
        },
        [screenToFlowPosition, nodes, edges, setNodes, setEdges, getEdge, getNode, dispatch, dragOverEdgeId, getDroppableEdgeIdFromPoint],
    );

    const onAddNode = useCallback((parentNode) => {

        const currentParent = getNode(parentNode.id) || parentNode;
        const newNodeId = uuidv4();
        const newNode = {
            id: newNodeId,
            type: 'subject',
            position: { x: currentParent.position.x, y: currentParent.position.y + 130 },
            data: {
                general_properties: {
                    label: 'subject',
                    description: '',
                    subjectConstraints: []
                },
                warning: true,
                warning_text: ["No subject constraint found. Please add subject constraint to this node."],
            }
        };

        const newEdge = {
            id: `${parentNode.id}-${newNodeId}`,
            source: parentNode.id,
            sourceHandle: "bottom-source",
            target: newNodeId,
            targetHandle: "top-target",
            style: { stroke: defaultEdgeColor, strokeWidth: 1, strokeDasharray: '5,5' }, // เส้นประ
            markerEnd: {
                type: MarkerType.ArrowClosed,
                width: 20,
                height: 20,
            },
        };

        setNodes((prev) => [...prev, newNode]);
        setEdges((prev) => [...prev, newEdge]);

        // }, [nodes, edges]);
    }, [getNode]);

    const onAddNodeById = useCallback((nodeId) => {
        const parentNode = getNode(nodeId);
        if (parentNode) {
            onAddNode(parentNode);
        }
    }, [onAddNode, getNode]);

    useEffect(() => {
        if (!selectedNode?.id || !Array.isArray(nodes) || nodes.length === 0) {
            return;
        }

        const matchedNode = nodes.find((node) => node?.id === selectedNode.id);
        if (!matchedNode) {
            return;
        }

        if (!isSameNodeForSelection(matchedNode, selectedNode)) {
            dispatch(setSelectedNodeRedux({ ...matchedNode }));
        }
    }, [nodes, selectedNode, dispatch])

    const onNodesChange = useCallback(
        async (changes) => {
            if (openPreviewDRGRef.current || openSimulatorDRGRef.current) {
                return;
            }
            // console.log("onNodesChange");
            setNodes((nodes) => {
                const oldNodes = nodes.map(
                    (node) => ({
                        ...node,
                        measured: {
                            width: node?.measured?.width || 150,
                            height: node?.measured?.height || 40,
                        }
                    })
                );
                return applyNodeChanges(changes, oldNodes);
            });
        },
        [setNodes],
    );

    const onEdgesChange = useCallback(
        (changes) => {
            if (openPreviewDRGRef.current || openSimulatorDRGRef.current) {
                return;
            }
            setEdges((eds) => applyEdgeChanges(changes, eds));
        },
        [setEdges],
    );

    const checkAttributeSelections = (object_attr) => {
        const hasChecked = object_attr.some(item => item.checked);
        if (hasChecked) {
            return true;
        } else {
            return false;
        }
    };

    // const handleCheckInheritData = async (targetNode, sourceNode) => {

    //     if (targetNode?.data?.general_properties?.resource_schema === undefined || sourceNode?.data?.general_properties?.resource_schema === undefined) {
    //         return nodes;
    //     }
    //     const target_attributes = checkAttributeSelections(targetNode?.data?.general_properties?.resource_schema);
    //     const source_attributes = checkAttributeSelections(sourceNode?.data?.general_properties?.resource_schema);

    //     if (target_attributes && !source_attributes && targetNode.type !== 'root') { // target มีค่า checked ที่เป็น true อยู่ และ source มีค่า checked = false ทั้งหมด
    //         const updatedStatusNodes = nodes.map((node) => {
    //             if (node?.id === sourceNode?.id) {
    //                 const inhertitAttributes = targetNode?.data?.general_properties?.resource_schema.map((attr) => (
    //                     {
    //                         ...attr,
    //                         checked: attr.checked
    //                     }
    //                 )).sort((a, b) => a.name.localeCompare(b.name));
    //                 return {
    //                     ...node,
    //                     data: {
    //                         ...node.data,
    //                         general_properties: {
    //                             ...node.data.general_properties,
    //                             resource_schema: inhertitAttributes
    //                         }
    //                     }
    //                 };
    //             }
    //             return node;
    //         });
    //         return updatedStatusNodes;
    //     } else if (!target_attributes && source_attributes && targetNode.type !== 'root') { // target มีค่า checked = false ทั้งหมด และ source มีค่า checked ที่เป็น true อยู่
    //         const updatedStatusNodes = nodes.map((node) => {
    //             if (node?.id === targetNode?.id) {
    //                 const inhertitAttributes = sourceNode?.data?.general_properties?.resource_schema.map((attr) => (
    //                     {
    //                         ...attr,
    //                         checked: attr.checked
    //                     }
    //                 )).sort((a, b) => a.name.localeCompare(b.name));
    //                 return {
    //                     ...node,
    //                     data: {
    //                         ...node.data,
    //                         general_properties: {
    //                             ...node.data.general_properties,
    //                             resource_schema: inhertitAttributes
    //                         }
    //                     }
    //                 };
    //             }
    //             return node;
    //         });
    //         return updatedStatusNodes;
    //     } else {
    //         return nodes; // ไม่ทำการสืบทอดใด ๆ
    //     }
    // };

    const getReachableNodes = (graph, startNodeId) => {
        const visited = new Set();

        if (!startNodeId || !graph[startNodeId]) {
            return visited;
        }

        const stack = [startNodeId];
        while (stack.length > 0) {
            const currentNodeId = stack.pop();
            if (visited.has(currentNodeId)) {
                continue;
            }

            visited.add(currentNodeId);
            const neighbors = graph[currentNodeId] || [];

            for (let i = 0; i < neighbors.length; i += 1) {
                const nextNodeId = neighbors[i];
                if (!visited.has(nextNodeId)) {
                    stack.push(nextNodeId);
                }
            }
        }

        return visited;
    };

    const updateNodeStatus = async (nodes, edges, subjectNodeId, authorizeNodeId) => {
        const adjacencyList = {};
        const reverseAdjacencyList = {};

        nodes.forEach((node) => {
            if (!node?.id) return;
            adjacencyList[node.id] = [];
            reverseAdjacencyList[node.id] = [];
        });

        edges.forEach((edge) => {
            if (!edge?.source || !edge?.target) return;
            if (adjacencyList[edge.source] && reverseAdjacencyList[edge.target]) {
                adjacencyList[edge.source].push(edge.target);
                reverseAdjacencyList[edge.target].push(edge.source);
            }
        });

        const subjectReachableNodes = getReachableNodes(adjacencyList, subjectNodeId);
        const authorizeUpstreamNodes = getReachableNodes(reverseAdjacencyList, authorizeNodeId);

        return nodes.map(node => {
            if (node?.id === subjectNodeId || node?.id === authorizeNodeId) {
                return { ...node };
            } else {
                const isSubjectConnected = subjectReachableNodes.has(node?.id);
                const isAuthorizeConnected = authorizeUpstreamNodes.has(node?.id);

                let warning = node.data.warning;
                let warning_text = node.data.warning_text;
                let error = node.data.error;
                let error_text = node.data.error_text;

                if (isSubjectConnected && isAuthorizeConnected) {
                    warning = false; // เชื่อมครบทั้ง subject และ authorize
                    warning_text = [];
                } else {
                    warning = true;
                    warning_text = ["No subject or Authorized Data node connection. Please connect this node to both nodes."]; // ยังไม่ได้เชื่อมทั้ง subject และ authorize 
                }

                return {
                    ...node,
                    data: {
                        ...node.data,
                        warning: warning,
                        warning_text: warning_text,
                        error: error,
                        error_text: error_text
                    },
                };
            }

        });
    };

    const handleConditionValidate = (node, updateNodes, rootNode) => {
        let error_text = [...(node?.data?.error_text || [])];

        // let error_text = [];
        const hasLabelError = node?.data?.general_properties?.label === ""; // ตรวจสอบกรณีไม่ใส่ชื่อ
        error_text = error_text.filter(text => text !== "Please assign a name to this node.");

        if (hasLabelError && node?.data?.warning === false) {
            error_text.push("Please assign a name to this node.");
        }

        error_text = error_text.filter(text => (text !== "No conditions set up." && text !== "No conditions set. Please add at least one condition."));
        const isEmptyCondition = node?.data?.general_properties?.conditions.length === 0; // ตรวจสอบกรณีไม่ได้สร้าง condition

        if (isEmptyCondition && node?.data?.warning === false) {
            error_text.push("No conditions set. Please add at least one condition.");
        }

        error_text = error_text.filter(text => (text !== "Invalid condition." && text !== "Condition invalid. Please correct it."));

        if (!isEmptyCondition) {

            const resolvedRootNode = rootNode || updateNodes?.find(n => n.type === 'root');
            if (!resolvedRootNode?.data?.general_properties?.resource_schema) {
                return {
                    ...node,
                    data: {
                        ...node.data,
                        error: error_text.length > 0,
                        error_text: error_text
                    }
                }
            }
            const resourceSchema = resolvedRootNode?.data?.general_properties?.resource_schema || [];
            const resourceTypeByName = new Map(resourceSchema.map((attr) => [attr.name, attr.data_type]));

            const checkConditionError = node?.data?.general_properties?.conditions.map(condition => {
                const checkSubConditionError = condition.subConditions.map(sub => {
                    const { attr_error_text, data_type_error_text, operator_error_text, ...sub_without_error } = sub;
                    const hasAttribute = resourceTypeByName.has(sub?.resourceAttribute?.name);
                    if (!hasAttribute) {
                        return {
                            ...sub_without_error,
                            attr_error_text: "Attribute no longer available. Please select another."
                        }
                    } else {
                        // ตรวจสอบ data type ของ attribute ที่เลือกใน condition กับ resource schema ใน authorized data ว่าตรงกันหรือไม่
                        const newDataType = resourceTypeByName.get(sub?.resourceAttribute?.name);
                        // const new_group_type = dataTypes.find(dt => dt.similar_type.includes(newDataType));
                        // const new_operators = new_group_type ? new_group_type.operators : [];

                        let resourceAttr = {
                            ...sub_without_error,
                            resourceAttribute: {
                                ...sub_without_error.resourceAttribute,
                                // data_type: newDataType,
                                // operators: new_operators,
                                // type: new_group_type ? new_group_type.type : undefined
                            }
                        }

                        if (sub?.resourceAttribute?.data_type !== newDataType) {
                            resourceAttr = {
                                ...resourceAttr,
                                attr_error_text: `Attribute type changed. Expected ${newDataType}.`
                            }
                        }

                        // if (sub?.value_property?.value_type === 'Constant') {
                        //     const typeValidator = createValidator(newDataType);
                        //     const isInNotInOp = isInOperator(sub?.operator);
                        //     const isArrayValue = Array.isArray(sub?.value_property?.value);

                        //     let validateResult;
                        //     if (isInNotInOp && isArrayValue) {
                        //         // For IN/NOT IN operators with array value, accept without strict validation
                        //         validateResult = {
                        //             valid: true,
                        //             parsed: sub?.value_property?.value,
                        //             error: null
                        //         };
                        //     } else {
                        //         validateResult = typeValidator.validate(sub?.value_property?.value);
                        //     }

                        //     const isOperatorMismatch = !isInNotInOp && !isOperatorSupported(resourceAttr?.resourceAttribute?.operators, sub?.operator);

                        //     if (isOperatorMismatch) {
                        //         resourceAttr = {
                        //             ...resourceAttr,
                        //             operator_error_text: "Operator for attribute type invalid."
                        //         }
                        //     }

                        //     // For IN/NOT IN operators with array value, bypass validation error
                        //     if (!validateResult.valid && isInNotInOp) {
                        //         resourceAttr = {
                        //             ...resourceAttr,
                        //             value_property: {
                        //                 ...resourceAttr.value_property,
                        //                 value: sub?.value_property?.value,
                        //                 constant_mode: "multiple"
                        //             }
                        //         }
                        //     } else if (!validateResult.valid) {
                        //         resourceAttr = {
                        //             ...resourceAttr,
                        //             data_type_error_text: validateResult.error,
                        //         }
                        //     } else {
                        //         resourceAttr = {
                        //             ...resourceAttr,
                        //             value_property: {
                        //                 ...resourceAttr.value_property,
                        //                 value: validateResult.parsed,
                        //                 constant_mode: Array.isArray(validateResult.parsed) ? "multiple" : "single"
                        //             }
                        //         }
                        //     }
                        // }

                        return resourceAttr;
                    }
                })
                const hasSubError = checkSubConditionError.some(sub => sub.attr_error_text || sub.data_type_error_text || sub.operator_error_text);
                if (hasSubError) {
                    return {
                        ...condition,
                        subConditions: checkSubConditionError,
                        error: true,
                    }
                } else {
                    const { error, ...condition_without_error } = condition;
                    return {
                        ...condition_without_error,
                        subConditions: checkSubConditionError
                    };
                }
            });

            const hasError = checkConditionError.some(condition => condition.error === true);

            if (hasError) {
                error_text.push("Condition invalid. Please correct it.");
            }

            return {
                ...node,
                data: {
                    ...node.data,
                    general_properties: {
                        ...node.data.general_properties,
                        conditions: checkConditionError
                    },
                    error: error_text.length > 0,
                    error_text: error_text
                }
            }

        }

        return {
            ...node,
            data: {
                ...node.data,
                error: error_text.length > 0,
                error_text: error_text
            }
        }
    };

    const handleSelectionValidate = (node) => {
        let error = false;
        let error_text = [];

        const attr_selection = node.data.general_properties.resource_schema;
        const hasSelected = checkAttributeSelections(attr_selection);
        if (!hasSelected && node?.data?.warning === false) {
            error_text = error_text.filter(text => (text !== "No attribute selected." && text !== "No attribute selected. Please select an attribute."));
            error_text.push("No attribute selected. Please select an attribute.");
            return {
                ...node,
                data: {
                    ...node.data,
                    error: error_text.length > 0,
                    error_text: error_text
                },
            }
        } else {

            let isNode = node;

            if (node?.data?.general_properties?.changedDataTypes?.length > 0) {
                const updatedSelection = isNode?.data?.general_properties?.resource_schema.map(attr => {
                    const found = isNode?.data?.general_properties?.changedDataTypes.find(item => item.name === attr.name);
                    if (found) {
                        return {
                            ...attr,
                            data_type: found.original_data_type
                        };
                    } else {
                        return attr;
                    }
                });
                isNode = {
                    ...isNode,
                    data: {
                        ...isNode.data,
                        general_properties: {
                            ...isNode.data.general_properties,
                            resource_schema: updatedSelection,
                            changedDataTypes: []
                        }
                    }
                }
            }

            if (node?.data?.general_properties?.removedColumns?.length > 0) {
                const has_Selected = checkAttributeSelections(isNode?.data?.general_properties?.removedColumns);
                if (has_Selected) {
                    error_text = error_text.filter(text => (
                        text !== "The attribute you selected has been deleted. Please choose a different option."
                        && text !== "Attribute no longer available. Please select another."
                    ));
                    error = true;
                    error_text.push("Attribute no longer available. Please select another.");
                    return {
                        ...isNode,
                        data: {
                            ...isNode.data,
                            error: error_text.length > 0,
                            error_text: error_text
                        },
                    }
                } else {
                    const removedNames = new Set(node?.data?.general_properties?.removedColumns.map(attr => attr.name));
                    const update_attr = isNode?.data?.general_properties?.resource_schema.filter(attr => !removedNames.has(attr.name)).sort((a, b) => a.name.localeCompare(b.name));
                    error = false;
                    error_text = [];
                    return {
                        ...node,
                        data: {
                            ...node.data,
                            error: error,
                            error_text: error_text,
                            general_properties: {
                                ...node.data.general_properties,
                                resource_schema: update_attr
                            }
                        },
                    }
                }
            } else {
                error = false;
                error_text = [];
                return {
                    ...isNode,
                    data: {
                        ...isNode.data,
                        error: error,
                        error_text: error_text
                    },
                }
            }

        }
    };

    const handleValidatePrivacyError = (data) => {
        const validatedPrivacyConfig = data.map((privacy) => {
            if (privacy?.attribute?.name !== "" && privacy?.visible_length !== "") {
                return {
                    ...privacy,
                    error: false,
                    error_text: []
                }
            } else {
                return {
                    ...privacy,
                    error: true,
                    error_text: ["Please complete all required fields."]
                }
            }
        });
        return validatedPrivacyConfig;
    };

    const handleCheckPrivacyNode = (nodeValidate) => {
        let error_text = [...(nodeValidate?.data?.error_text || [])];
        const isValidated = handleValidatePrivacyError(nodeValidate.data.general_properties.privacies);
        const hasError = isValidated.some(obj => obj.error === true);

        error_text = error_text.filter(text => (
            text !== "Privacy configuration is incomplete."
            && text !== "Privacy configuration incomplete. Please complete it."
        ));

        if (hasError) {
            error_text.push("Privacy configuration incomplete. Please complete it.");
            const node_privacy = {
                ...nodeValidate,
                data: {
                    ...nodeValidate.data,
                    general_properties: {
                        ...nodeValidate.data.general_properties,
                        privacies: isValidated
                    },
                    error: hasError,
                    error_text: error_text
                }
            }
            return node_privacy;
        } else {
            const node_privacy = {
                ...nodeValidate,
                data: {
                    ...nodeValidate.data,
                    general_properties: {
                        ...nodeValidate.data.general_properties,
                        privacies: isValidated
                    },
                    error: error_text.length > 0,
                    error_text: error_text
                }
            }
            return node_privacy;
        }

    };

    const handlePrivacyValidateAttributes = (node, rootNode) => {
        const privacyConfig = node?.data?.general_properties?.privacies;
        const newAttributes = rootNode?.data?.general_properties?.resource_schema;
        const attributeByName = new Map((newAttributes || []).map((attr) => [attr.name, attr]));

        const updatedPrivacies = privacyConfig.map((privacy) => {
            if (privacy.attribute.name === "") {
                return {
                    ...privacy,
                    attr_error: false,
                    attr_error_text: []
                };
            } else {
                const match = attributeByName.get(privacy.attribute.name);
                if (!match) {
                    return {
                        ...privacy,
                        attr_error: true,
                        attr_error_text: ["Attribute no longer available. Please select another."]
                    };
                }
                return {
                    ...privacy,
                    attr_error: false,
                    attr_error_text: []
                };
            }
        });

        const { addedColumns, changedDataTypes, removedColumns, ...restGeneralProperties } = node.data.general_properties;

        let error_text = [...(node?.data?.error_text || [])];
        const hasError = updatedPrivacies.some(privacy => privacy.attr_error === true);
        error_text = error_text.filter(text => (
            text !== "No attribute selected for privacy settings."
            && text !== "Attribute no longer available. Please select another."
        ));

        if (hasError) {
            error_text.push("Attribute no longer available. Please select another.");
        }

        return {
            ...node,
            data: {
                ...node.data,
                general_properties: {
                    ...restGeneralProperties,
                    privacies: updatedPrivacies,
                    resource_schema: newAttributes
                },
                error: hasError,
                error_text: error_text
            }
        };
    };

    const checkPrivacyNodeErrorFromSelection = (paths, nodes, privacyNode) => {
        let shouldDeleteAttribute = true;
        let errorText = new Set(privacyNode.data.error_text || []);
        let updatePrivacy = privacyNode;

        for (const pathObj of paths) {
            const path = pathObj.pathNodes;
            if (!path.includes(privacyNode.id)) continue;

            const selectionNode = nodes.find(n => n.type === "selection" && path.includes(n.id));
            if (!selectionNode) continue;

            // let hasError = false;

            if (updatePrivacy.data?.general_properties?.privacies) {
                updatePrivacy.data.general_properties.privacies.forEach(privacy => {
                    if (privacy?.attributeFromSelectionError) {
                        const isAttributeFound = selectionNode.data?.general_properties?.resource_schema?.some(
                            attr => attr.checked === true && attr.name === privacy?.attributeFromSelectionError
                        );

                        if (!isAttributeFound) {
                            // hasError = true;
                            shouldDeleteAttribute = false;
                        }
                    }
                });
            }
        }

        if (shouldDeleteAttribute && updatePrivacy.data?.general_properties?.privacies) {
            const newGeneralProperties = { ...privacyNode.data.general_properties };
            newGeneralProperties.privacies = newGeneralProperties.privacies.map(privacy => {
                if (privacy?.attributeFromSelectionError) {
                    const { attributeFromSelectionError, ...newPrivacy } = privacy;
                    return newPrivacy;
                }
                return privacy;
            });
            updatePrivacy.data.general_properties = newGeneralProperties;
        }

        if (updatePrivacy.data?.general_properties?.privacies?.some(p => p.attributeFromSelectionError)) {
            errorText.add("Attribute no longer available. Please select another.");
        } else {
            errorText.delete("Attribute no longer available. Please select another.");
        }

        updatePrivacy.data.error_text = Array.from(errorText);
        updatePrivacy.data.error = errorText.size > 0;

        return updatePrivacy;
    }

    const handlePrivacyValidate = (node, updateNodes, updateEdges, rootNode) => {
        const resolvedRootNode = rootNode || updateNodes.find((n) => n.type === 'root');
        let privacyTargetNode = { ...node };
        let error_text = [...(privacyTargetNode?.data?.error_text || [])];
        error_text = error_text.filter(text => (
            text !== "Privacy configuration for the data is missing."
            && text !== "No privacy configurations set. Please configure data privacy."
        ));
        privacyTargetNode = {
            ...privacyTargetNode,
            data: {
                ...privacyTargetNode.data,
                error: error_text.length > 0,
                error_text: error_text
            }
        }
        if (privacyTargetNode?.data?.general_properties?.privacies.length === 0 && privacyTargetNode?.data?.warning === false) {
            error_text.push("No privacy configurations set. Please configure data privacy.");
            return {
                ...privacyTargetNode,
                data: {
                    ...privacyTargetNode.data,
                    error: error_text.length > 0,
                    error_text: error_text
                },
            }
        } else {
            const nodeValidateResource = handlePrivacyValidateAttributes(privacyTargetNode, resolvedRootNode);
            const nodeValidate = handleCheckPrivacyNode(nodeValidateResource); // ตรวจสอบความถูกต้องของ privacy configuration

            // if (nodeValidate?.data?.error) {
            //     const findPaths = handleClickFindTargetPath(privacyTargetNode.id, updateNodes, updateEdges);
            //     const updatedNode = checkPrivacyNodeErrorFromSelection(findPaths, updateNodes, nodeValidate);
            //     console.log("Node after checking privacy error from selection:", updatedNode);
            //     return updatedNode;
            // }

            const findPaths = handleClickFindTargetPath(privacyTargetNode.id, updateNodes, updateEdges);
            const updatedNode = checkPrivacyNodeErrorFromSelection(findPaths, updateNodes, nodeValidate);
            return updatedNode;

            // return nodeValidate;
        }
    };

    const handleConjunctionValidate = (node) => {
        let error_text = [...(node?.data?.error_text || [])];
        const hasError = node?.data?.general_properties?.label === "";
        error_text = error_text.filter(text => text !== "Please assign a name to this node.");

        if (hasError) {
            error_text.push("Please assign a name to this node.");
        }

        return {
            ...node,
            data: {
                ...node.data,
                error: hasError,
                error_text: error_text
            }
        };
    };

    const handleSubjectValidate = (node, updateNodes, rootNode) => {
        let error_text = [...(node?.data?.error_text || [])];
        const hasError = node?.data?.general_properties?.label === "";
        error_text = error_text.filter(text => text !== "Please assign a name to this node.");

        if (hasError) {
            error_text.push("Please assign a name to this node.");
        }

        error_text = error_text.filter(text => (text !== "Invalid constraint." && text !== "Constraint invalid. Please correct it."));

        const isEmptySubjectConstraint = node?.data?.general_properties?.subjectConstraints.length === 0;
        if (isEmptySubjectConstraint && !node?.root) {
            return {
                ...node,
                data: {
                    ...node.data,
                    error: false,
                    error_text: [],
                    warning: true,
                    warning_text: ["No subject constraint found. Please add subject constraint to this node."]
                }
            }
        } else {
            const resolvedRootNode = rootNode || updateNodes?.find(n => n.type === 'root');
            const subjectSchema = resolvedRootNode?.data?.general_properties?.subject_schema || [];
            const subjectTypeByName = new Map(subjectSchema.map((attr) => [attr.name, attr.attributeType]));

            const checkSubjectConstraintError = node.data.general_properties.subjectConstraints.map(constraint => {
                const checkSubConstraintError = constraint.child.map(sub => {
                    const { attr_error_text, data_type_error_text, operator_error_text, ...sub_without_error } = sub;
                    const hasAttribute = subjectTypeByName.has(sub?.l_operand?.name);
                    if (!hasAttribute) {
                        return {
                            ...sub_without_error,
                            attr_error_text: "Attribute no longer available. Please select another."
                        }
                    } else {
                        // ตรวจสอบ attribute type ของ attribute ที่เลือกใน suject constraint กับ subject schema ใน authorized data ว่าตรงกันหรือไม่
                        const newDataType = subjectTypeByName.get(sub?.l_operand?.name);
                        // const new_group_type = dataTypes.find(dt => dt.similar_type.includes(newDataType));
                        // const new_operators = new_group_type ? new_group_type.operators : [];

                        let subjectAttr = {
                            ...sub_without_error,
                            l_operand: {
                                ...sub_without_error.l_operand,
                                // attributeType: newDataType,
                                // operators: new_operators,
                                // type: new_group_type ? new_group_type.type : undefined
                            }
                        }

                        if (sub?.l_operand?.type !== newDataType) {
                            subjectAttr = {
                                ...subjectAttr,
                                attr_error_text: `Attribute type changed. Expected ${newDataType}.`
                            }
                        }

                        // const normalizedNewDataType = String(newDataType).toLowerCase();
                        // const normalizedOperator = String(sub_without_error.operator).toLowerCase();
                        // const isArrayIncludesOperator = (normalizedNewDataType === 'array' || normalizedNewDataType.includes('array'))
                        //     && (normalizedOperator === 'includes' || normalizedOperator.includes('includes'));
                        // const isArrayTypeWithStringValue = (normalizedNewDataType === 'array' || normalizedNewDataType.includes('array'))
                        //     && !Array.isArray(sub_without_error.r_operand)
                        //     && sub_without_error.r_operand !== '';

                        // // Check for IN/NOT IN operators
                        // const isInNotInOp = isInOperator(sub_without_error.operator);
                        // const isArrayValue = Array.isArray(sub_without_error.r_operand);

                        // const typeValidator = createLDAPValidator(newDataType);
                        // let validateResult;

                        // if (isArrayIncludesOperator && isArrayTypeWithStringValue) {
                        //     // For array type with string value, accept without strict validation
                        //     validateResult = {
                        //         valid: true,
                        //         parsed: sub_without_error.r_operand,
                        //         error: null
                        //     };
                        // } else if (isInNotInOp && isArrayValue) {
                        //     // For IN/NOT IN operators with array value, accept without strict validation
                        //     validateResult = {
                        //         valid: true,
                        //         parsed: sub_without_error.r_operand,
                        //         error: null
                        //     };
                        // } else {
                        //     validateResult = typeValidator.validate(sub_without_error.r_operand);
                        // }

                        // const isOperatorMismatch = !isInNotInOp && !isArrayIncludesOperator && !isOperatorSupported(subjectAttr.l_operand.operators, sub.operator);

                        // if (isOperatorMismatch) {
                        //     subjectAttr = {
                        //         ...subjectAttr,
                        //         operator_error_text: "Operator for attribute type invalid."
                        //     }
                        // }

                        // // For IN/NOT IN operators with array value, bypass validation error
                        // if (!validateResult.valid && (isInNotInOp || isArrayIncludesOperator)) {
                        //     subjectAttr = {
                        //         ...subjectAttr,
                        //         r_operand: sub_without_error.r_operand
                        //     }
                        // } else if (!validateResult.valid) {
                        //     subjectAttr = {
                        //         ...subjectAttr,
                        //         data_type_error_text: validateResult.error
                        //     }
                        // } else {
                        //     subjectAttr = {
                        //         ...subjectAttr,
                        //         r_operand: validateResult.parsed
                        //     }
                        // }

                        return subjectAttr;
                    }
                });

                const hasSubError = checkSubConstraintError.some(sub => sub.attr_error_text || sub.data_type_error_text || sub.operator_error_text);
                if (hasSubError) {
                    return {
                        ...constraint,
                        child: checkSubConstraintError,
                        error: true,
                    }
                } else {
                    const { error, ...constraint_without_error } = constraint;
                    return {
                        ...constraint_without_error,
                        child: checkSubConstraintError
                    };
                }

            });

            const hasError = checkSubjectConstraintError.some(constraint => constraint.error === true);

            if (hasError) {
                error_text.push("Constraint invalid. Please correct it.");
            }

            return {
                ...node,
                data: {
                    ...node.data,
                    general_properties: {
                        ...node.data.general_properties,
                        subjectConstraints: checkSubjectConstraintError
                    },
                    error: error_text.length > 0,
                    error_text: error_text,
                    warning: false,
                    warning_text: []
                }
            }

        }

        return {
            ...node,
            data: {
                ...node.data,
                error: error_text.length > 0,
                error_text: error_text,
                warning: false,
                warning_text: []
            }
        };
    };

    const handleRootValidate = (node, nodes, updateEdges) => {
        let error_text = [...(node?.data?.error_text || [])];
        const hasError = node?.data?.general_properties?.label === "";
        error_text = error_text.filter(text => (
            text !== "Please assign a name to this node."
            && text !== "This node must be connected to subject."
            && text !== "No subject node connection. Please connect this node to a subject node."
        ));

        if (hasError) {
            error_text.push("Please assign a name to this node.");
        }

        const allUpstreamNodes = getAllUpstreamNodes(node, nodes, updateEdges); // ฟังก์ชันที่ใช้ในการดึงข้อมูล upstream nodes ทั้งหมดของ root node

        const hasConnectionToSubject = allUpstreamNodes.some(upNode => upNode.type === 'subject');

        if (!hasConnectionToSubject) {
            error_text.push("No subject node connection. Please connect this node to a subject node.");
        }

        return {
            ...node,
            data: {
                ...node.data,
                error: (hasError || !hasConnectionToSubject) ? true : false,
                error_text: error_text
            }
        };
    };

    const handleValidateData = async (updateNodes, updateEdges) => {
        const rootNode = updateNodes?.find((node) => node.type === 'root');

        const updatedErrorNodes = updateNodes.map((node) => {
            if (node.type === 'root') {
                const updatedNode = handleRootValidate(node, updateNodes, updateEdges);
                return updatedNode;
            } else if (node.type === 'subject') {
                const updatedNode = handleSubjectValidate(node, updateNodes, rootNode);
                return updatedNode;
            } else if (node.type === 'condition') {
                const updatedNode = handleConditionValidate(node, updateNodes, rootNode);
                return updatedNode;
            } else if (node.type === 'selection') {
                const updatedNode = handleSelectionValidate(node);
                return updatedNode;
            } else if (node.type === 'privacy') {
                const updatedNode = handlePrivacyValidate(node, updateNodes, updateEdges, rootNode);
                return updatedNode;
            } else if (node.type === 'conjunction') {
                const updatedNode = handleConjunctionValidate(node);
                return updatedNode;
            } else {
                return { ...node }
            }
        });

        return updatedErrorNodes;
    };

    const shouldConnect = (sourceNode, targetNode) => {
        if (sourceNode.type === "subject" && targetNode.type !== "subject") {
            return true;
        } else if (sourceNode.type !== "subject" && targetNode.type !== "subject") {
            return true;
        } else {
            return false;
        }
    };

    const shouldShowHandle = (start_node, node) => {
        if (start_node.type === 'subject') {
            if (start_node.type !== node.type) {
                return true;
            } else {
                return false;
            }
        } else if (start_node.type !== 'subject') {
            if (node.type !== 'subject') {
                return true;
            } else {
                return false;
            }
        }
    };

    const onConnectStart = (event, params) => {
        if (openPreviewDRGRef.current || openSimulatorDRGRef.current) {
            return false; // ป้องกันการเริ่มการเชื่อมต่อ
        }
        if (!isReconnectRef.current) { // ตรวจสอบว่าไม่ใช่ reconnect
            const start_node = getNode(params.nodeId);
            setNodes(nodes => nodes.map((node) => ({
                ...node,
                data: { ...node.data, showHandle: shouldShowHandle(start_node, node) }
            })));
        }
    };

    const onConnect = useCallback(
        async (params) => {
            // console.log("onConnect edge:", params);
            if (openPreviewDRGRef.current || openSimulatorDRGRef.current) {
                return; // ป้องกันการเชื่อมต่อ
            }

            if (params.source === params.target) { // ไม่ให้เชื่อมเข้าตัวมันเอง
                return;
            }
            const isDuplicate = edges.some(
                (edge) => edge.source === params.source && edge.target === params.target
            );
            if (isDuplicate) { // เช็กเส้นซ้ำ
                return;
            }

            const sourceNode = getNode(params.source);
            const targetNode = getNode(params.target);

            if (!sourceNode || !targetNode) {
                return;
            }

            if (sourceNode.type !== 'subject' && targetNode.type === 'subject') {
                return;
            } else if (sourceNode.type === 'subject' && targetNode.type === 'subject') {
                return;
            }

            const edgeStyle = sourceNode.type === 'subject' ? { stroke: defaultEdgeColor, strokeWidth: 1, strokeDasharray: '5,5' } : { stroke: defaultEdgeColor, strokeWidth: 1 };

            const newEdge = {
                ...params,
                id: `${sourceNode.id}-${targetNode.id}`,
                style: edgeStyle,
                markerEnd: {
                    type: MarkerType.ArrowClosed,
                    width: 20,
                    height: 20,
                    color: defaultEdgeColor
                }
            };

            const newEdges = addEdge(newEdge, edges);

            const find_target_path = handleClickFindTargetPath(targetNode.id, nodes, newEdges);

            // filter เอาเฉพาะ path ที่เกี่ยวกับ source,target node และ new edge เพื่อใช้ในการตรวจสอบ validation ต่างๆ
            const filtered_path = find_target_path.filter(item => {
                const isPath = item.pathNodes.includes(sourceNode.id) && item.pathNodes.includes(targetNode.id);
                const hasNewEdge = item.pathEdges.includes(newEdge.id);
                return isPath && hasNewEdge;
            });

            // console.log("filtered_path: ", filtered_path);

            const valid_duplicate_selection = validateDuplicateSelection(filtered_path, nodes);

            if (!valid_duplicate_selection) {
                dispatch(setSnackbarMessage({
                    open: true,
                    message: "Duplicate selection found.",
                    status: "error",
                }));
                return;
            }

            const valid_privacy_attr_duplicate = validateDuplicatePrivacyAttributes(filtered_path, nodes);

            if (!valid_privacy_attr_duplicate) {
                dispatch(setSnackbarMessage({
                    open: true,
                    message: "Duplicate privacy attributes found.",
                    status: "error",
                }));
                return;
            }

            const valid_conflict_selection_privacy = validateSelectionAttributesInPath(filtered_path, nodes, newEdge);

            if (!valid_conflict_selection_privacy) {
                dispatch(setSnackbarMessage({
                    open: true,
                    message: "Conflict between selection and privacy attributes found.",
                    status: "error",
                }));
                return;
            }

            setEdges(newEdges);

            const subjectNodeId = nodes.find(node => (node.type === 'subject' && node.root === true));
            const authorizeNodeId = nodes.find(node => node.type === 'root');

            if (!subjectNodeId || !authorizeNodeId) {
                return;
            }

            const inherit_node = inheritPrivacyAttributesToSelection(find_target_path, nodes);

            const updatedStatusNodes = await updateNodeStatus(inherit_node, newEdges, subjectNodeId?.id, authorizeNodeId?.id); // ตรวจสอบการเชื่อมต่อข้อมูล
            const updatedNodes = await handleValidateData(updatedStatusNodes, newEdges); // ตรวจสอบความถูกต้องข้อมูล
            handleValidationFile(updatedNodes, newEdges); // อัปเดต validation ของไฟล์ตาม error ของ node

            setNodes(updatedNodes);

        },
        [dispatch, setEdges, setNodes, nodes, edges, getNode],
    );

    const shouldShowHandleConnectEnd = (nodeSelected, node) => {
        if (nodeSelected?.id === node?.id && nodeSelected?.type !== 'root') {
            return true;
        } else {
            return false;
        }
    };

    const onConnectEnd = () => {
        setTimeout(() => {
            isReconnectRef.current = false; // รีเซ็ต reconnect หลัง event จบ
            setNodes(nodes => nodes.map(node => ({ ...node, data: { ...node.data, showHandle: shouldShowHandleConnectEnd(selectedNode, node) } })));

            // setNodes((prev) =>
            //     prev.map((nd) => ({ ...nd, data: { ...nd.data, showHandle: selectedNode.id === nd.id ? true : false } }))
            // )
        }, 0);
    };

    const onNodeDoubleClick = useCallback(() => {
        if (openSimulatorDRGRef.current || openPreviewDRGRef.current) {
            return; // ป้องกันการเปิด properties panel ขณะเปิด simulator
        }
        dispatch(setOpenDrawerRight(true));
    }, [dispatch]);

    const shouldShowReConnectSubjectStart = (node) => {
        if (node.type !== 'root') {
            return true;
        } else {
            return false;
        }
    };

    const shouldShowReConnectStart = (node) => {
        if (node.type !== 'subject') {
            return true;
        } else {
            return false;
        }
    };

    const onReconnectStart = (event, edge, handleType) => {
        if (openPreviewDRGRef.current || openSimulatorDRGRef.current) {
            return;
        }

        isReconnectRef.current = true; // ตั้งค่า reconnect
        const sourceNode = getNode(edge.source);
        const targetNode = getNode(edge.target);
        if ((sourceNode.type === 'subject' && edge.sourceHandle === 'bottom-source') ||
            (targetNode.type === 'subject' && edge.targetHandle === 'top-target')) {
            return setNodes(nodes => nodes.map((node) => ({
                ...node,
                data: { ...node.data, showHandle: false }
            })));
        }
        if (handleType === 'target') {
            return setNodes(nodes => nodes.map((node) => ({
                ...node,
                data: { ...node.data, showHandle: shouldShowReConnectSubjectStart(node) }
            })));
        } else {
            return setNodes(nodes => nodes.map((node) => ({
                ...node,
                data: { ...node.data, showHandle: shouldShowReConnectStart(node) }
            })));
        }
    };

    const onReconnect = useCallback(async (oldEdge, newConnection) => {

        if (openPreviewDRGRef.current || openSimulatorDRGRef.current) {
            return;
        }

        if (newConnection.source === newConnection.target) {
            return;
        }

        const sourceNode = getNode(newConnection.source);
        const targetNode = getNode(newConnection.target);

        if (!sourceNode || !targetNode) {
            return;
        }

        if ((sourceNode.type === 'subject' && newConnection.sourceHandle === 'bottom-source') ||
            (targetNode.type === 'subject' && newConnection.targetHandle === 'top-target')) {
            return;
        }

        const validateNodes = shouldConnect(sourceNode, targetNode);
        // const newEdges = reconnectEdge(oldEdge, newConnection, edges);
        const newEdges = edges.map((edge) => {
            if (edge.id === oldEdge.id) {
                return {
                    ...edge,
                    id: `${sourceNode.id}-${targetNode.id}`,
                    source: newConnection.source,
                    sourceHandle: newConnection.sourceHandle,
                    target: newConnection.target,
                    targetHandle: newConnection.targetHandle,
                };
            }
            return edge;
        });

        let find_target_path;
        let updateEdges = newEdges;

        if (validateNodes) {

            updateEdges = newEdges.map((edge) => {
                const sourceType = getNode(edge.source);
                const edgeStyle = sourceType.type === 'subject' ? { stroke: defaultEdgeColor, strokeWidth: 1, strokeDasharray: '5,5' } : { stroke: defaultEdgeColor, strokeWidth: 1 };

                const newEdge = {
                    ...edge,
                    selected: false,
                    style: edgeStyle,
                    markerEnd: {
                        type: MarkerType.ArrowClosed,
                        width: 20,
                        height: 20,
                        color: defaultEdgeColor
                    }
                };
                return newEdge;
            });

            find_target_path = handleClickFindTargetPath(targetNode.id, nodes, updateEdges);

            // filter เอาเฉพาะ path ที่เกี่ยวกับ source และ target เพื่อใช้ในการตรวจสอบ validation ต่างๆ
            const filtered_path = find_target_path.filter(item => {
                const isPath = item.pathNodes.includes(sourceNode.id) && item.pathNodes.includes(targetNode.id);
                const hasNewEdge = item.pathEdges.includes(`${sourceNode.id}-${targetNode.id}`);
                return isPath && hasNewEdge;
            });

            const valid_duplicate_selection = validateDuplicateSelection(filtered_path, nodes);

            if (!valid_duplicate_selection) {
                dispatch(setSnackbarMessage({
                    open: true,
                    message: "Duplicate selection found.",
                    status: "error",

                }));
                return;
            }

            const valid_privacy_attr_duplicate = validateDuplicatePrivacyAttributes(filtered_path, nodes);

            if (!valid_privacy_attr_duplicate) {
                dispatch(setSnackbarMessage({
                    open: true,
                    message: "Duplicate privacy attributes found.",
                    status: "error",

                }));
                return;
            }

            const valid_conflict_selection_privacy = validateSelectionAttributesInPath(filtered_path, nodes);

            if (!valid_conflict_selection_privacy) {
                dispatch(setSnackbarMessage({
                    open: true,
                    message: "Conflict between selection and privacy attributes found.",
                    status: "error",

                }));
                return;
            }

            setEdges(updateEdges);
        }

        const subjectNodeId = nodes.find(node => (node.type === 'subject' && node.root === true));
        const authorizeNodeId = nodes.find(node => node.type === 'root');

        if (!subjectNodeId || !authorizeNodeId) {
            return;
        }

        const inherit_node = inheritPrivacyAttributesToSelection(find_target_path, nodes);

        const updatedStatusNodes = await updateNodeStatus(inherit_node, updateEdges, subjectNodeId?.id, authorizeNodeId?.id); // ตรวจสอบการเชื่อมต่อข้อมูล
        const updatedNodes = await handleValidateData(updatedStatusNodes, updateEdges); // ตรวจสอบความถูกต้องข้อมูล
        handleValidationFile(updatedNodes, updateEdges); // อัปเดต validation ของไฟล์ตาม error ของ node

        setNodes(updatedNodes);


    }, [dispatch, nodes, edges, getNode, setEdges, setNodes]);

    const handleConfirm = async () => {
        setLoadingConfirmDialog(true);
        // Guard clause: เช็ค regulatoryFile ก่อน (ป้องกัน undefined error)
        if (!regulatoryFile?.regulatory_diagram_id) {
            console.warn('regulatoryFile is not loaded yet');
            dispatch(setSnackbarMessage({
                open: true,
                message: 'Failed: Regulatory file data is not ready',
                status: 'error',
            }));
            setLoadingConfirmDialog(false);
            return;
        }

        const now = Date.now();

        let owner_id = regulatoryFile?.owner_id || regulatoryFile?.creator_id; // ใช้ owner_id หรือ creator_id ถ้าไม่มี owner_id
        let owner = regulatoryFile?.owner || regulatoryFile?.creator_name; // ใช้ owner หรือ creator_name ถ้าไม่มี owner
        let created_at = regulatoryFile?.created_at || regulatoryFile?.create_timestamp; // ใช้ created_at หรือ create_timestamp ถ้าไม่มี created_at
        if (!regulatoryFile?.owner_id) {
            owner_id = regulatoryFile?.updater_id; // ใช้ creator_id แทน owner_id
        }
        if (!regulatoryFile?.owner) {
            owner = regulatoryFile?.updater; // ใช้ creator_name แทน owner
        }
        if (!regulatoryFile?.created_at) {
            created_at = now; // ใช้ create_timestamp แทน created_at
        }

        let update = {
            ...regulatoryFile,
            regulatory_diagram_id: regulatoryFile?.regulatory_diagram_id,
            regulatory_diagram_name: regulatoryFile?.regulatory_diagram_name,
            owner_id: owner_id, // ใช้ owner_id แทน creator_id
            owner: owner, // ใช้ owner แทน creator_name
            updater_id: profile?.inum,
            updater: profile?.displayName,
            created_at: created_at, // ใช้ created_at แทน create_timestamp
            updater_at: now,
            description: regulatoryFile?.description,
            validation: regulatoryFile?.validation || [], // ใช้ regulatoryFile.validation หรือ default เป็น array ว่าง
        }

        // console.log("Regulatory file after update:", update);

        const updateNodesWithUsedAttributes = await findUsedAttributes(nodes);
        const updateNodesWithUsedSubjectAttributes = await findUsedSubjectAttributesInDRG(updateNodesWithUsedAttributes);
        const persistedDiagram = createRegulatoryDomainDiagram(updateNodesWithUsedSubjectAttributes, edges);

        if (persistedDiagram.nodes.length > 0 || persistedDiagram.edges.length > 0) {
            update = {
                ...update,
                diagram: persistedDiagram
            };
        }

        update = checkDiagramError(update);

        // console.log("Update object to be sent to API:", update); // ตรวจสอบข้อมูลที่ส่งไปยัง API

        const gzippedBody = gzip(JSON.stringify(update)); // บีบอัดข้อมูลด้วย gzip

        const requestOptions = {
            method: "PUT",
            headers: {
                'Content-Type': 'application/json',
                'Content-Encoding': 'gzip', // Inform the server the body is gzipped
            },
            body: gzippedBody
            // body: JSON.stringify(update)
        };

        try {
            const result = await onSaveDiagram(requestOptions);
            if (result) {
                setNodes(updateNodesWithUsedSubjectAttributes)
                const updatedRegulatoryFile = {
                    ...regulatoryFileRef.current,
                    diagram: {
                        ...regulatoryFileRef.current.diagram,
                        nodes: update.diagram.nodes,
                        edges: update.diagram.edges
                    }
                };
                localStorage.setItem('regulatory-file', JSON.stringify(updatedRegulatoryFile));
                // setSavedFlow({ nodes: update.diagram.nodes, edges: update.diagram.edges }); // บันทึก currentFlow เป็น savedFlow
                dispatch(setHasDiagramChange(false));
                dispatch(setSnackbarMessage({
                    open: true,
                    message: "Regulatory diagram updated successfully",
                    status: "success",

                }));

                const requestPostOptions = {
                    method: "POST",
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(update)
                };
                const validate_response = await fetch(process.env.REACT_APP_API_VALIDATION_URL + `/verify/validate-drg?project_id=${project?.project_id}`, requestPostOptions);
                if (validate_response.status !== 200) {
                    console.error(validate_response.status);
                    // dispatch(setSnackbarMessage({
                    //     open: true,
                    //     message: `Error status: ${validate_response.status}`,
                    //     status: "error",

                    // }));
                }

                setStatusUpdate(true); //refresh drg files
                // setOpenNotificationBeforePreview(false);
                setOpenNotificationBeforeChangeFile(false);

            } else {
                throw new Error('Failed to save the diagram');
            }
        } catch (error) {
            dispatch(setSnackbarMessage({
                open: true,
                message: error.message || 'Failed to save the diagram',
                status: 'error',
            }));
        } finally {
            setLoadingConfirmDialog(false);
        }

    };

    const handleSavedAndOpenPreview = async (nodeId) => {
        // Guard clause 1: เช็ค nodeId ก่อน (ป้องกัน undefined error)
        if (!nodeId) {
            console.warn('nodeId is undefined');
            dispatch(setSnackbarMessage({
                open: true,
                message: 'Failed: No subject selected for preview',
                status: 'error',
            }));
            return;
        }

        // Guard clause 2: เช็ค regulatoryFile ก่อน (ป้องกัน undefined error)
        if (!regulatoryFile?.regulatory_diagram_id) {
            console.warn('regulatoryFile is not loaded yet');
            dispatch(setSnackbarMessage({
                open: true,
                message: 'Failed: Regulatory file data is not ready',
                status: 'error',
            }));
            return;
        }

        // Guard clause 3: เช็ค profile ก่อน (ป้องกัน undefined error)
        if (!profile) {
            console.warn('profile data is not available');
            dispatch(setSnackbarMessage({
                open: true,
                message: 'Failed: User profile is not available',
                status: 'error',
            }));
            return;
        }

        const now = Date.now();

        let owner_id = regulatoryFile?.owner_id || regulatoryFile?.creator_id; // ใช้ owner_id หรือ creator_id ถ้าไม่มี owner_id
        let owner = regulatoryFile?.owner || regulatoryFile?.creator_name; // ใช้ owner หรือ creator_name ถ้าไม่มี owner
        let created_at = regulatoryFile?.created_at || regulatoryFile?.create_timestamp; // ใช้ created_at หรือ create_timestamp ถ้าไม่มี created_at
        if (!regulatoryFile?.owner_id) {
            owner_id = regulatoryFile?.updater_id; // ใช้ creator_id แทน owner_id
        }
        if (!regulatoryFile?.owner) {
            owner = regulatoryFile?.updater; // ใช้ creator_name แทน owner
        }
        if (!regulatoryFile?.created_at) {
            created_at = now; // ใช้ create_timestamp แทน created_at
        }

        let update = {
            ...regulatoryFile,
            regulatory_diagram_id: regulatoryFile?.regulatory_diagram_id,
            regulatory_diagram_name: regulatoryFile?.regulatory_diagram_name,
            owner_id: owner_id, // ใช้ owner_id แทน creator_id
            owner: owner, // ใช้ owner แทน creator_name
            updater_id: profile?.inum,
            updater: profile?.displayName,
            created_at: created_at, // ใช้ created_at แทน create_timestamp
            updater_at: now,
            description: regulatoryFile?.description,
            validation: regulatoryFile?.validation || [], // ใช้ regulatoryFile.validation หรือ default เป็น array ว่าง
        }

        // console.log("Regulatory file after update:", update);

        const updateNodesWithUsedAttributes = await findUsedAttributes(nodes);
        const updateNodesWithUsedSubjectAttributes = await findUsedSubjectAttributesInDRG(updateNodesWithUsedAttributes)
        const persistedDiagram = createRegulatoryDomainDiagram(updateNodesWithUsedSubjectAttributes, edges);

        if (persistedDiagram.nodes.length > 0 || persistedDiagram.edges.length > 0) {
            update = {
                ...update,
                diagram: persistedDiagram
            };
        }

        update = checkDiagramError(update);

        const gzippedBody = gzip(JSON.stringify(update)); // บีบอัดข้อมูลด้วย gzip

        const requestOptions = {
            method: "PUT",
            headers: {
                'Content-Type': 'application/json',
                'Content-Encoding': 'gzip', // Inform the server the body is gzipped
            },
            body: gzippedBody
        };

        try {
            const result = await onSaveDiagram(requestOptions);
            if (result) {
                setNodes(updateNodesWithUsedSubjectAttributes) // อัปเดต nodes ด้วยข้อมูลที่ใช้จริง
                const updatedRegulatoryFile = {
                    ...regulatoryFileRef.current,
                    diagram: {
                        ...regulatoryFileRef.current.diagram,
                        nodes: update.diagram.nodes,
                        edges: update.diagram.edges
                    }
                };
                localStorage.setItem('regulatory-file', JSON.stringify(updatedRegulatoryFile));
                // setSavedFlow({ nodes: update.diagram?.nodes, edges: update.diagram?.edges }); // บันทึก currentFlow เป็น savedFlow
                dispatch(setHasDiagramChange(false));

                setStatusUpdate(true); //refresh drg files

                const requestPostOptions = {
                    method: "POST",
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(update)
                };
                const validate_response = await fetch(process.env.REACT_APP_API_VALIDATION_URL + `/verify/validate-drg?project_id=${project?.project_id}`, requestPostOptions);
                if (validate_response.status === 200) {
                    // const result = await validate_response.json();
                    // console.log("Validation result:", result);
                } else {
                    console.error(validate_response.status);
                    dispatch(setSnackbarMessage({
                        open: true,
                        message: `Validation error: ${validate_response.status}`,
                        status: "error",
                    }));
                }

                // ตรวจสอบ path ก่อนเปิด preview
                const valid_preview = checkPathForPreview(nodeId);
                if (valid_preview) {
                    onPreviewDRG(nodeId);
                } else {
                    dispatch(setSnackbarMessage({
                        open: true,
                        message: "Cannot preview because the path is incomplete.",
                        status: "error",
                    }));
                }
            } else {
                throw new Error('Failed to save the diagram before preview');
            }
        } catch (error) {
            dispatch(setSnackbarMessage({
                open: true,
                message: error.message || 'Failed to save the diagram before preview',
                status: 'error',
            }));
        }
    };

    const onSaveDiagram = async (requestOptions) => {
        try {
            const response = await fetch(process.env.REACT_APP_API_URL + `/regulatory-diagram/update-regulatory/${regulatoryFile?.regulatory_diagram_id}`, requestOptions);
            if (!response.ok) {
                throw response.status;
            }
            return true;
        } catch (error) {
            console.error('Error is saving status', error);
            return false;
        }
    }

    handleConfirmRef.current = handleConfirm;

    useEffect(() => {
        const handleToolbarSaveRequest = (event) => {
            const diagramType = event?.detail?.diagramType;
            if (diagramType && diagramType !== DIAGRAM_TYPES.REGULATORY) {
                return;
            }

            if (openPreviewDRGRef.current || openSimulatorDRGRef.current) {
                return;
            }

            handleConfirmRef.current?.();
        };

        window.addEventListener(DIAGRAM_TOOLBAR_SAVE_EVENT, handleToolbarSaveRequest);
        return () => window.removeEventListener(DIAGRAM_TOOLBAR_SAVE_EVENT, handleToolbarSaveRequest);
    }, []);

    const handleValidateNode = async (isValidNode, remakeEdges) => {
        const rootNode = isValidNode.find(node => node.type === 'root');

        if (rootNode) {
            const rootAttributes = rootNode.data.general_properties.resource_schema;

            const updatedArray = isValidNode.map(node => {
                if (node.type !== 'root' && node.type !== 'subject') {
                    const changedDataTypes = [];
                    const addedColumns = [];
                    const removedColumns = [];
                    const nodeAttributes = node?.data?.general_properties?.resource_schema;

                    nodeAttributes.forEach(attr => {
                        const match = rootAttributes.find(
                            rootAttr => rootAttr.name === attr.name
                        );

                        if (!match) {
                            // หาก name ไม่มีใน rootAttributes
                            removedColumns.push(attr);
                        } else if (match.data_type !== attr.data_type) {
                            // หาก data_type ไม่ตรงกัน
                            changedDataTypes.push({
                                name: attr.name,
                                original_data_type: match.data_type,
                                current_data_type: attr.data_type
                            });
                        }
                    });

                    // ตรวจสอบ name ที่มีใน rootAttributes แต่ไม่มีใน attr
                    rootAttributes.forEach(rootAttr => {
                        if (!nodeAttributes.some(attr => attr.name === rootAttr.name)) {
                            const added_attr = {
                                ...rootAttr,
                                new_attr: true
                            };
                            addedColumns.push(added_attr);
                        }
                    });

                    const updated_resource_schema = nodeAttributes.map(attr => {
                        const found = rootAttributes.find(item => item.name === attr.name);
                        if (found) {
                            return attr;
                        } else {
                            if (attr.checked) {
                                return attr;
                            }
                        }
                    }).filter(Boolean).sort((a, b) => a.name.localeCompare(b.name)); // Filter out undefined values

                    return {
                        ...node,
                        data: {
                            ...node.data,
                            general_properties: {
                                ...node.data.general_properties,
                                changedDataTypes, // เก็บ column_name ที่ data_type ไม่ตรงกัน
                                addedColumns, // เก็บ column_name ที่ถูกเพิ่มมา
                                removedColumns, // เก็บ column_name ที่ถูกลบออก
                                resource_schema: updated_resource_schema // Update resource_schema
                            }
                        }
                    };
                }
                return node;
            });

            const updatedNodes = await handleValidateData(updatedArray, remakeEdges || edges);
            handleValidationFile(updatedNodes, edges); // อัปเดต validation ของไฟล์ตาม error ของ node
            setNodes(updatedNodes);

        } else {
            console.error("No root object found!");
        }
    };

    useEffect(() => {
        async function updatePropertieData(nds, eds) {
            // console.log("Updating");
            await handleValidateNode(nds, eds);
            setUpdateProperties(false);
        };
        if (updateProperties && nodes.length > 0) {
            updatePropertieData(nodes, edges);
        }
    }, [updateProperties, nodes, edges, handleValidateNode]);

    const nodesWithUpdatedData = useMemo(() => {
        const isReadOnly = openPreviewDRG || openSimulatorDRG;
        const nextDraggable = !isReadOnly;
        const nextSelectable = !isReadOnly;
        const nextCache = new Map();

        const mappedNodes = nodes.map((node) => {
            const nextDeletable = isReadOnly ? false : !node?.root;
            const cachedNode = nodeViewCacheRef.current.get(node.id);

            if (
                cachedNode
                && cachedNode.sourceNode === node
                && cachedNode.draggable === nextDraggable
                && cachedNode.selectable === nextSelectable
                && cachedNode.deletable === nextDeletable
            ) {
                nextCache.set(node.id, cachedNode);
                return cachedNode.viewNode;
            }

            const viewNode = {
                ...node,
                draggable: nextDraggable,
                selectable: nextSelectable,
                deletable: nextDeletable
            };

            nextCache.set(node.id, {
                sourceNode: node,
                draggable: nextDraggable,
                selectable: nextSelectable,
                deletable: nextDeletable,
                viewNode
            });

            return viewNode;
        });

        nodeViewCacheRef.current = nextCache;
        return mappedNodes;
    }, [nodes, openPreviewDRG, openSimulatorDRG]);

    const onDelete = useCallback(async (deletedData) => {
        const { edges: deletedEdges, nodes: deletedNodes } = deletedData;
        let newEdges = edges;
        let newNodes = nodes;

        const subjectNodeId = nodes.find(node => node.type === 'subject' && node.root === true);
        const authorizeNodeId = nodes.find(node => node.type === 'root');

        if (deletedEdges.length > 0) {
            newEdges = edges.filter(edge => !deletedEdges.some(deletedEdge => deletedEdge.id === edge.id));
            setEdges(newEdges);
        }

        if (deletedNodes.length > 0) {
            newNodes = nodes.filter(node => !deletedNodes.some(item => node.id === item.id));
            setOpenProperties(false);
            dispatch(clearSelectedNodeRedux());
        }

        if (deletedEdges.length > 0 || deletedNodes.length > 0) {
            const updatedStatusNodes = await updateNodeStatus(newNodes, newEdges, subjectNodeId?.id, authorizeNodeId?.id);
            const updatedNodes = await handleValidateData(updatedStatusNodes, newEdges);
            handleValidationFile(updatedNodes, newEdges); // อัปเดต validation ของไฟล์ตาม error ของ node
            setNodes(updatedNodes);
            setUpdateProperties(true);
        }
        clearActiveHandles();
    }, [nodes, edges, setEdges, setNodes, dispatch, clearActiveHandles]);

    const handleResourceUpdateClose = () => {
        setOpenResourceUpdate(false);
    };

    const handleNotificationDPEClose = () => {
        setOpenNotificationDpe(false);
    };

    // const onEdgeClick = (event, edge) => {
    //     console.log('onEdgeClick', openPreviewDRG);
    //     if (!openPreviewDRG) {
    //         setNodes(nodes => nodes.map((node) => ({
    //             ...node,
    //             data: { ...node.data, showHandle: false }
    //         })));
    //         setFirstCreate(false);
    //         setOpenProperties(false);
    //         dispatch(clearSelectedNodeRedux());
    //     }
    // };

    const onEdgeClick = useCallback((event, edge) => {
        // console.log('onEdgeClick', edge);
        if (openPreviewDRGRef.current || openSimulatorDRGRef.current) {
            event.preventDefault();
            event.stopPropagation(); // ปิดการคลิก edge
            return;
        }
        setLastClickedNodeId(null);
        setLastClickedEdgeId(edge.id);
        setSelectedNodeIds([]);
        setSelectedEdgeIds([edge.id]);
        setNodes(nodes => nodes.map((node) => {
            const isSourceNode = node.id === edge.source;
            const isTargetNode = node.id === edge.target;

            return {
                ...node,
                selected: false,
                data: {
                    ...node.data,
                    activeSourceHandleId: isSourceNode ? edge.sourceHandle : null,
                    activeTargetHandleId: isTargetNode ? edge.targetHandle : null,
                    showHandle: isSourceNode || isTargetNode
                }
            };
        }));
        // setEdges((currentEdges) => currentEdges.map((currentEdge) => ({
        //     ...currentEdge,
        //     selected: currentEdge.id === edge.id
        // })));
        setEdges((currentEdges) => {
            const clickedEdge = currentEdges.find((e) => e.id === edge.id);
            const otherEdges = currentEdges.filter((e) => e.id !== edge.id);

            if (!clickedEdge) return currentEdges;

            const updatedOtherEdges = otherEdges.map((e) => ({
                ...e,
                selected: false,
                reconnectable: false
            }));

            return [
                ...updatedOtherEdges,
                { ...clickedEdge, selected: true, reconnectable: true } // ให้ขยับได้แค่เส้นนี้คนเดียว ✅
            ];
        });
        setFirstCreate(false);
        setOpenProperties(false);
        dispatch(clearSelectedNodeRedux());
    }, [dispatch, setEdges, setNodes, setFirstCreate]);

    const handleSettingResource = useCallback(() => {
        setOpenNotificationDpe(false);
        const root_nodes = nodes.find((node) => node.type === 'root');
        setNodes((nds) =>
            nds.map((node) => ({ ...node, selected: node.type === 'root' }))
        );
        dispatch(setSelectedNodeRedux(root_nodes));
        setOpenProperties(true);
        dispatch(setOpenDrawerRight(true));
    }, [nodes, dispatch, setNodes]);

    const handleResourceUpdate = async () => {
        setResourceUpdateDetail(undefined);
        await handleConfirm();
        handleResourceUpdateClose();
    };

    const validateDuplicateSelection = (paths, nodes) => {
        const nodeMap = new Map(nodes.map(node => [node.id, node.type]));

        for (const path of paths) {
            const selectionNodes = path?.pathNodes.filter(nodeId => nodeMap.get(nodeId) === "selection");
            if (selectionNodes.length > 1) {
                return false; // ถ้า path ไหนมี selection node มากกว่า 1 ตัว ให้ return false ทันที
            }
        }
        return true;
    };

    const validateDuplicatePrivacyAttributes = (paths, nodes) => {
        const nodeMap = new Map(nodes.map(node => [node.id, node]));

        for (const path of paths) {
            // ดึง node ที่เป็น privacy ออกจาก path
            const privacyNodes = path.pathNodes
                .map(nodeId => nodeMap.get(nodeId))
                .filter(node => node?.type === "privacy");

            if (privacyNodes.length > 1) {
                // ใช้ Set เพื่อตรวจสอบ attribute.name ที่ซ้ำกัน
                const attributeSet = new Set();

                for (const node of privacyNodes) {
                    const privacies = node.data?.general_properties?.privacies || [];

                    for (const privacy of privacies) {
                        const attributeName = privacy.attribute?.name;
                        if (attributeName) {
                            if (attributeSet.has(attributeName)) {
                                return false; // เจอ attribute.name ซ้ำกัน
                            }
                            attributeSet.add(attributeName);
                        }
                    }
                }
            }
        }
        return true; // ถ้าไม่มี attribute.name ซ้ำกันเลย return true
    };

    const inheritPrivacyAttributesToSelection = (paths, nodes) => {
        const nodeMap = new Map(nodes.map(node => [node.id, node]));
        let updatedNodes = [...nodes];

        for (const path of paths) {
            // ดึง selection และ privacy nodes ใน path
            const selectionNodes = path.pathNodes.map(nodeId => nodeMap.get(nodeId)).filter(node => node?.type === "selection");
            const privacyNodes = path.pathNodes.map(nodeId => nodeMap.get(nodeId)).filter(node => node?.type === "privacy");

            // ต้องมีทั้ง selection และ privacy ใน path นั้นๆ
            if (selectionNodes.length === 0 || privacyNodes.length === 0) continue;

            for (const selectionNode of selectionNodes) {
                const attributeSelection = selectionNode.data?.general_properties?.resource_schema || [];

                // เช็กว่ามี checked เป็น true อยู่แล้วหรือไม่
                const hasChecked = attributeSelection.some(attr => attr.checked);
                if (hasChecked) continue; // ถ้ามี checked อยู่แล้วข้ามไป

                // รวม attribute.name จาก privacy nodes ทั้งหมดใน path
                const privacyAttributes = new Set();
                for (const privacyNode of privacyNodes) {
                    const privacies = privacyNode.data?.general_properties?.privacies || [];
                    privacies.forEach(privacy => {
                        if (privacy.attribute?.name) {
                            privacyAttributes.add(privacy.attribute.name);
                        }
                    });
                }

                // อัปเดต resource_schema ใน selection node
                const updatedAttributeSelection = attributeSelection.map(attr => ({
                    ...attr,
                    checked: privacyAttributes.has(attr.name) ? true : attr.checked
                }));

                // อัปเดต selection node ใน nodes
                updatedNodes = updatedNodes.map(node =>
                    node.id === selectionNode.id
                        ? {
                            ...node,
                            data: {
                                ...node.data,
                                general_properties: {
                                    ...node.data.general_properties,
                                    resource_schema: updatedAttributeSelection
                                }
                            }
                        }
                        : node
                );
            }
        }
        return updatedNodes;
    };

    const validateSelectionAttributesInPath = (paths, nodes) => {
        // console.log("validateSelectionAttributesInPath: ", paths);
        const nodeMap = new Map(nodes.map(node => [node.id, node]));

        for (const path of paths) {

            // หา selection และ privacy nodes ใน path
            const selectionNodes = path.pathNodes.map(nodeId => nodeMap.get(nodeId)).filter(node => node?.type === "selection");
            const privacyNodes = path.pathNodes.map(nodeId => nodeMap.get(nodeId)).filter(node => node?.type === "privacy");

            // 1. ถ้าไม่มี selection node ใน path หรือมีเกิน 1 ให้ข้าม path นี้
            if (selectionNodes.length === 0) continue;
            if (selectionNodes.length > 1) return false;

            const selectionNode = selectionNodes[0];

            // 2. ถ้าไม่มี privacy node ใน path ให้ข้าม path นี้ได้เลย
            if (privacyNodes.length === 0) continue;

            // 3. ดึง attribute ที่ checked = true จาก selection node
            const selectionAttributes = new Set(
                selectionNode.data?.general_properties?.resource_schema
                    ?.filter(attr => attr.checked)
                    ?.map(attr => attr.name) || []
            );

            // 3.1 ถ้า attribute.checked ของ selection node เป็น false ทั้งหมด → ข้าม path นี้
            if (selectionAttributes.size === 0) continue;

            // 4. รวม attribute.name จาก privacy nodes
            const privacyAttributes = new Set();
            for (const privacyNode of privacyNodes) {
                const privacies = privacyNode.data?.general_properties?.privacies || [];
                privacies.forEach(privacy => {
                    if (privacy.attribute?.name) {
                        privacyAttributes.add(privacy.attribute.name);
                    }
                });
            }

            // 5. เช็กว่า privacyAttributes ทุกตัวต้องมีใน selectionAttributes
            for (const privacyAttr of privacyAttributes) {
                if (!selectionAttributes.has(privacyAttr)) {
                    return false; // มี attribute ของ privacy ที่ไม่มีใน selection
                }
            }
        }
        return true; // ทุก path ที่มี selection + privacy ผ่านเงื่อนไข
    };

    const onNodeDragStart = useCallback((e, node, nodes) => {
        if (openPreviewDRGRef.current || openSimulatorDRGRef.current) {
            return;
        }
        // console.log("onNodeDragStart: ", node)
        setNodes((prev) =>
            prev.map((nd) => ({ ...nd, data: { ...nd.data, showHandle: false } }))
        )
        dispatch(setSelectedNodeRedux(node));
        setFirstCreate(false);
        setOpenProperties(true);
    }, [dispatch, setNodes]);

    const validatePaths = (paths, nodes) => {
        // ตรวจสอบว่า paths ทั้งหมดเป็น "full path" หรือไม่
        const allFullPath = paths.every(path => path.pathType === "full path");

        if (!allFullPath) return false;

        for (const pathData of paths) {
            for (const nodeId of pathData.pathNodes) {
                const node = nodes.find(n => n.id === nodeId);
                if (node?.data?.error || node?.data?.warning) {
                    return false; // ถ้ามี error หรือ warning → return false
                }
            }
        }

        return true; // ผ่านทุกเงื่อนไข return true
    };

    const checkPathForPreview = (targetId) => {

        const resultPaths = handleClickFindTargetPath(targetId, nodesRef.current, edgesRef.current);

        return validatePaths(resultPaths, nodesRef.current);
    };

    const onPreviewDRG = async (nodeId) => {
        if (!regulatoryFileRef.current?.regulatory_diagram_id || !projectRef.current?.project_id) {
            console.error("Missing regulatory_diagram_id or project_id.");
            dispatch(setSnackbarMessage({
                open: true,
                message: "Cannot preview because the required data is missing.",
                status: "error",
            }));
            return;
        }
        const response = await fetch(`${process.env.REACT_APP_API_G2D}/preview?regulatory_id=${regulatoryFileRef.current?.regulatory_diagram_id}&subject_id=${nodeId}&project_id=${projectRef.current?.project_id}`);
        if (response.ok) {
            const result = await response.json();
            // handleHighlight(nodeId);
            handleHighlightPreview(nodeId);
            handlePreviewDataChange(result);
            dispatch(setOpenDrawerRight(false));
            dispatch(setOpenPreviewDRG(true));
        } else {
            dispatch(setSnackbarMessage({
                open: true,
                message: "Cannot preview because the path is incomplete.",
                status: "error",

            }));
        }
    };

    // 🔧 ไม่ใช้ useCallback - สร้าง function ปกติเพื่อให้ access latest data/closure
    const handlePreview = async (nodeId) => {
        if (openSimulatorDRGRef.current) {
            return;
        }

        clearActiveHandles();
        if (hasDiagramChangeRef.current) {
            handleSavedAndOpenPreview(nodeId);
        } else {
            const valid_preview = checkPathForPreview(nodeId);
            // console.log("valid_preview", valid_preview);
            if (valid_preview) {
                onPreviewDRG(nodeId);
            } else {
                dispatch(setSnackbarMessage({
                    open: true,
                    message: "Cannot preview because the path is incomplete.",
                    status: "error",

                }));
            }
        }
    };

    // 🔧 Update ref โดยตรงเพื่อให้ nodeTypes สามารถ reference ได้
    handlePreviewRef.current = handlePreview;

    const summarizeNodesAndEdges = (pathsWithEdges, nodes, edges) => {
        // ใช้ Set เพื่อเก็บ nodeId และ edgeId ที่ไม่ซ้ำ
        const nodeIdsSet = new Set();
        const edgeIdsSet = new Set();

        pathsWithEdges.forEach(({ pathNodes, edges }) => {
            pathNodes.forEach(nodeId => nodeIdsSet.add(nodeId));
            edges.forEach(edge => edgeIdsSet.add(edge.id));
        });

        // ดึง node ที่เกี่ยวข้องจาก nodeIdsSet
        const filteredNodes = nodes.filter(node => nodeIdsSet.has(node.id));

        // ดึง edge ที่เกี่ยวข้องจาก edgeIdsSet
        const filteredEdges = edges.filter(edge => edgeIdsSet.has(edge.id));

        return {
            nodes: filteredNodes,
            edges: filteredEdges
        };
    };

    const updateHighlightPathColor = (nodes, edges, summarizedData, targetId) => {
        // 🔧 สร้าง Set เพื่อให้ lookup เป็น O(1) แทนการใช้ .some() ที่เป็น O(n)
        const highlightedNodeIds = new Set(summarizedData.nodes.map(n => n.id));
        const highlightedEdgeIds = new Set(summarizedData.edges.map(e => e.id));

        const updatedNodes = nodes.map(node => {
            const isHighlighted = highlightedNodeIds.has(node.id);
            return {
                ...node,
                selected: isHighlighted,
                data: {
                    ...node.data,
                    showHandle: (isHighlighted && node.id === targetId && !openPreviewDRGRef.current) ? true : false
                }
            };
        });

        const updatedEdges = edges.map(edge => {
            const isHighlighted = highlightedEdgeIds.has(edge.id);
            return {
                ...edge,
                selected: isHighlighted,
                style: { ...(edge.style || {}), strokeWidth: isHighlighted ? 1 : 1 }
            };
        });

        setNodes(updatedNodes);
        setEdges(updatedEdges);
    };

    const handleHighlight = useCallback((nodeId) => {
        const resultPaths = handleClickFindTargetPath(nodeId, nodesRef.current, edgesRef.current);
        const pathsWithEdges = findEdgesForPaths(resultPaths, edgesRef.current);
        const summarizedData = summarizeNodesAndEdges(pathsWithEdges, nodesRef.current, edgesRef.current);
        // console.log("summarizedData:", summarizedData);
        updateHighlightPathColor(nodesRef.current, edgesRef.current, summarizedData, nodeId);
    }, []); // 🔧 ไม่มี dependency เพราะใช้ refs ทั้งหมด

    // 🔧 Update ref โดยตรง ไม่ต้อง useEffect
    handleHighlightRef.current = handleHighlight;

    const nodeTypes = useMemo(() => ({
        // 🔧 Reference ไปยัง ref เพื่อให้ได้ latest handlePreview function ทุกครั้ง
        subject: (props) => <SubjectNode {...props} highlighted={props?.data?.highlighted} onPreviewClick={(...args) => handlePreviewRef.current?.(...args)} onNodeClick={onNodeClick} onNodeDoubleClick={onNodeDoubleClick} onAddNode={onAddNodeById} />,
        root: (props) => <RootNode {...props} highlighted={props?.data?.highlighted} onNodeClick={onNodeClick} />,
        condition: (props) => <ConditionNode {...props} highlighted={props?.data?.highlighted} onNodeClick={onNodeClick} onNodeDoubleClick={onNodeDoubleClick} />,
        selection: (props) => <SelectionNode {...props} highlighted={props?.data?.highlighted} onNodeClick={onNodeClick} />,
        privacy: (props) => <PrivacyNode {...props} highlighted={props?.data?.highlighted} onNodeClick={onNodeClick} />,
        conjunction: (props) => <ConjunctionNode {...props} highlighted={props?.data?.highlighted} onNodeClick={onNodeClick} />
    }), [onNodeClick, onNodeDoubleClick, onAddNodeById]); // 🔧 ไม่ depend handlePreview - ใช้ ref แทน

    useEffect(() => {
        const currentFile = getStorageJson('regulatory-file', {});
        if (!currentFile?.diagram) return;

        flowChangeTokenRef.current += 1;
        const currentToken = flowChangeTokenRef.current;

        if (flowChangeTimerRef.current) {
            clearTimeout(flowChangeTimerRef.current);
        }

        // Debounce expensive graph diffing to prevent stutter during dense interactions.
        flowChangeTimerRef.current = setTimeout(async () => {
            const originalFlow = {
                nodes: currentFile.diagram.nodes || [],
                edges: currentFile.diagram.edges || []
            };
            const isChanged = await hasFlowChanged(originalFlow, { nodes, edges });

            if (currentToken !== flowChangeTokenRef.current) {
                return;
            }

            dispatch(setHasDiagramChange(isChanged));
        }, 120);

        return () => {
            if (flowChangeTimerRef.current) {
                clearTimeout(flowChangeTimerRef.current);
                flowChangeTimerRef.current = null;
            }
        };
    }, [nodes, edges, dispatch]); // เช็กเมื่อ nodes/edges เปลี่ยน

    useEffect(() => {
        if (hasFileChange) {
            setOpenNotificationBeforeChangeFile(true);
            dispatch(setHasDRGFile(false));
        }
    }, [hasFileChange, dispatch]);

    const onChange = useCallback(({ nodes, edges }) => {
        // console.log('onChange triggered', nodes, edges);
        if ((openPreviewDRGRef.current || openSimulatorDRGRef.current) && nodes.length === 0 && edges.length === 0) {
            if (openPreviewDRGRef.current) {
                setNodes(previewCurrentRef.current.nodes);
                setEdges(previewCurrentRef.current.edges);
            }
            // สำหรับ simulator mode ไม่ reset เพราะมี highlight อยู่แล้ว
        }
    }, []);

    useOnSelectionChange({
        onChange,
    });

    const onSelectionStart = useCallback(() => {
        if (openPreviewDRGRef.current || openSimulatorDRGRef.current) {
            return;
        }
        // ล้างค่า selection ล่าสุดออก เพื่อบอกว่าผู้ใช้ต้องการทำงานกับ "กลุ่ม" ที่กำลังจะลากคุม
        setLastClickedNodeId(null);
        setLastClickedEdgeId(null);
    }, []);

    // อัปเดตรายการ Node และ Edge ที่ถูก Select ไว้เสมอ
    const onSelectionChange = useCallback(({ nodes, edges }) => {
        // console.log('onSelectionChange triggered', nodes, edges);
        if (openPreviewDRGRef.current || openSimulatorDRGRef.current) return;
        setSelectedNodeIds(nodes.map((n) => n.id));
        setSelectedEdgeIds((edges || []).map((edge) => edge.id));
    }, []);

    // useUnsavedChangesWarning(hasDiagramChange);

    const handleCustomDelete = useCallback(() => {
        if (openPreviewDRGRef.current || openSimulatorDRGRef.current) {
            return;
        }

        let targetNodesToDelete = [];
        let targetEdgesToDelete = [];

        const isNodeDeletable = (node) => node?.deletable !== false;

        if (lastClickedEdgeId) {
            // กรณีลบเฉพาะ Edge ล่าสุดที่คลิก
            targetEdgesToDelete = edges.filter(edge => edge.id === lastClickedEdgeId);
            setLastClickedEdgeId(null);
            setSelectedEdgeIds([]);
        }
        else if (lastClickedNodeId) {
            // กรณีลบเฉพาะ Node ล่าสุดที่คลิก
            targetNodesToDelete = nodes.filter(node => node.id === lastClickedNodeId && isNodeDeletable(node));

            // หาเส้น Edge ที่เชื่อมต่อกับ Node ที่กำลังจะโดนลบ
            targetEdgesToDelete = edges.filter(
                edge => targetNodesToDelete.some(node => node.id === edge.source || node.id === edge.target)
            );

            setLastClickedNodeId(null);
        }
        else if (selectedNodeIds.length > 0 || selectedEdgeIds.length > 0) {
            // กรณีลากคุม (Drag Selection) ลบหลายตัวทั้ง Node และ Edge
            targetNodesToDelete = nodes.filter(node => selectedNodeIds.includes(node.id) && isNodeDeletable(node));
            const deletableNodeIds = targetNodesToDelete.map(node => node.id);

            const connectedEdges = edges.filter(
                edge => deletableNodeIds.includes(edge.source) || deletableNodeIds.includes(edge.target)
            );
            const directlySelectedEdges = edges.filter(edge => selectedEdgeIds.includes(edge.id));

            targetEdgesToDelete = Array.from(
                new Map([...connectedEdges, ...directlySelectedEdges].map((edge) => [edge.id, edge])).values()
            );

            setSelectedNodeIds([]);
            setSelectedEdgeIds([]);
        }

        // ถ้ามีของที่จะลบ ให้ส่งต่อให้ฟังก์ชัน onDelete เดิมของคุณทำงาน
        if (targetNodesToDelete.length > 0 || targetEdgesToDelete.length > 0) {
            // สร้าง format deletedData ให้ตรงกับที่ onDelete ของคุณรอรับ
            onDelete({
                nodes: targetNodesToDelete,
                edges: targetEdgesToDelete
            });
        }

    }, [lastClickedEdgeId, lastClickedNodeId, selectedNodeIds, selectedEdgeIds, nodes, edges, onDelete]);

    // --- ดักจับคีย์บอร์ด Delete / Backspace ---
    useEffect(() => {
        const handleKeyDown = (event) => {
            if (openPreviewDRGRef.current || openSimulatorDRGRef.current) {
                return;
            }

            if (event.key === 'Backspace' || event.key === 'Delete') {
                const activeTag = document.activeElement?.tagName?.toLowerCase();
                if (activeTag === 'input' || activeTag === 'textarea') return;

                // เรียกตัวกลางให้ประมวลผลเป้าหมาย แล้วค่อยส่งไปให้ onDelete
                handleCustomDelete();
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [handleCustomDelete]);

    // ดักจับ "ระหว่าง" ที่โหนดกำลังถูกลาก
    const onNodeDrag = useCallback(
        (e, node) => {
            if (openPreviewDRGRef.current || openSimulatorDRGRef.current) {
                return;
            }
            // ค้นหา DOM Element ของ Node ที่กำลังลากอยู่
            const nodeDiv = document.querySelector(`.react-flow__node[data-id="${node.id}"]`);
            if (!nodeDiv) return;

            // หาพิกัด (X, Y) กึ่งกลางของโหนดนั้น
            const rect = nodeDiv.getBoundingClientRect();
            const centerX = rect.left + rect.width / 2;
            const centerY = rect.top + rect.height / 2;

            // ตรวจสอบว่าพิกัดกึ่งกลางนั้น ไปทับกับคลาส react-flow__edge-interaction ของเส้นใดๆ หรือไม่
            const nextEdgeId = getDroppableEdgeIdFromPoint(centerX, centerY);

            if (overlappedEdgeRef.current !== nextEdgeId) {
                overlappedEdgeRef.current = nextEdgeId;
                setDragOverEdgeId(nextEdgeId);
            }
        },
        [getDroppableEdgeIdFromPoint]
    );

    // ดักจับ "ตอนที่ปล่อยเมาส์" (ลากโหนดเสร็จแล้ว)
    const onNodeDragStop = useCallback(
        async (e, node) => {
            if (openPreviewDRGRef.current || openSimulatorDRGRef.current) {
                setDragOverEdgeId(null);
                overlappedEdgeRef.current = null;
                return;
            }

            const edgeId = overlappedEdgeRef.current;
            setDragOverEdgeId(null);
            overlappedEdgeRef.current = null;

            // ถ้าตอนปล่อยเมาส์ ไม่ได้ทับเส้นไหนอยู่เลย ก็ให้จบการทำงานแค่นี้
            if (!edgeId) return;

            const edge = getEdge(edgeId);
            if (!edge || node.id === edge.source || node.id === edge.target) return;

            const getDefaultEdgeStyle = (sourceType) => (
                sourceType === 'subject'
                    ? { stroke: defaultEdgeColor, strokeWidth: 1, strokeDasharray: '5,5' }
                    : { stroke: defaultEdgeColor, strokeWidth: 1 }
            );

            const sourceNode = getNode(edge.source);
            const targetNode = getNode(edge.target);

            if (!sourceNode || !targetNode) return;
            if (isSubjectToSubjectConnection(sourceNode, targetNode)) return;

            const isValidIncomingConnection = shouldConnect(sourceNode, node);
            const isValidOutgoingConnection = shouldConnect(node, targetNode);

            if (!isValidIncomingConnection || !isValidOutgoingConnection) {
                return;
            }

            const hasDuplicateIncomingEdge = edges.some(
                (currentEdge) => currentEdge.id !== edgeId && currentEdge.source === sourceNode.id && currentEdge.target === node.id
            );
            const hasDuplicateOutgoingEdge = edges.some(
                (currentEdge) => currentEdge.id !== edgeId && currentEdge.source === node.id && currentEdge.target === targetNode.id
            );

            if (hasDuplicateIncomingEdge || hasDuplicateOutgoingEdge) {
                return;
            }

            const droppedNodeCenter = getNodeCenterPoint(node);
            const sourceNodeCenter = getNodeCenterPoint(sourceNode);
            const targetNodeCenter = getNodeCenterPoint(targetNode);

            const incomingSourceHandle = getClosestHandleId(sourceNode, 'source', droppedNodeCenter, edge.sourceHandle);
            const incomingTargetHandle = getClosestHandleId(node, 'target', sourceNodeCenter, edge.targetHandle);
            const outgoingSourceHandle = getClosestHandleId(node, 'source', targetNodeCenter, 'bottom-source');
            const outgoingTargetHandle = getClosestHandleId(targetNode, 'target', droppedNodeCenter, edge.targetHandle);

            const incomingEdgeStyle = getDefaultEdgeStyle(sourceNode?.type);
            const outgoingEdgeStyle = getDefaultEdgeStyle(node?.type);
            const defaultMarkerEnd = {
                type: MarkerType.ArrowClosed,
                width: 20,
                height: 20,
                color: defaultEdgeColor
            };

            const updatedIncomingEdge = {
                ...edge,
                source: edge.source,
                sourceHandle: incomingSourceHandle,
                target: node.id,
                targetHandle: incomingTargetHandle,
                style: incomingEdgeStyle,
                markerEnd: defaultMarkerEnd,
            };

            const newOutgoingEdge = {
                id: `${node.id}-${edge.target}`,
                source: node.id,
                sourceHandle: outgoingSourceHandle,
                target: edge.target,
                targetHandle: outgoingTargetHandle,
                type: edge.type,
                animated: edge.animated,
                style: outgoingEdgeStyle,
                markerEnd: defaultMarkerEnd,
            };

            const updatedEdges = edges
                .filter((currentEdge) => currentEdge.id !== edgeId)
                .concat(updatedIncomingEdge, newOutgoingEdge);

            const find_target_path = handleClickFindTargetPath(targetNode.id, nodes, updatedEdges);

            // filter เอาเฉพาะ path ที่เกี่ยวกับ source และ target เพื่อใช้ในการตรวจสอบ validation ต่างๆ
            const filtered_path = find_target_path.filter(item => {
                const isPath = item.pathNodes.includes(sourceNode.id) && item.pathNodes.includes(targetNode.id);
                const hasNewEdge = item.pathEdges.includes(updatedIncomingEdge.id) && item.pathEdges.includes(newOutgoingEdge.id);
                return isPath && hasNewEdge;
            });

            const valid_duplicate_selection = validateDuplicateSelection(filtered_path, nodes);
            if (!valid_duplicate_selection) {
                dispatch(setSnackbarMessage({
                    open: true,
                    message: "Duplicate selection found.",
                    status: "error",
                }));
                return;
            }

            const valid_privacy_attr_duplicate = validateDuplicatePrivacyAttributes(filtered_path, nodes);
            if (!valid_privacy_attr_duplicate) {
                dispatch(setSnackbarMessage({
                    open: true,
                    message: "Duplicate privacy attributes found.",
                    status: "error",
                }));
                return;
            }

            const valid_conflict_selection_privacy = validateSelectionAttributesInPath(filtered_path, nodes);
            if (!valid_conflict_selection_privacy) {
                dispatch(setSnackbarMessage({
                    open: true,
                    message: "Conflict between selection and privacy attributes found.",
                    status: "error",
                }));
                return;
            }

            setEdges(updatedEdges);

            const subjectNodeId = nodes.find((currentNode) => currentNode.type === 'subject' && currentNode.root === true);
            const authorizeNodeId = nodes.find((currentNode) => currentNode.type === 'root');
            const inherit_node = inheritPrivacyAttributesToSelection(find_target_path, nodes);

            const updatedStatusNodes = (subjectNodeId && authorizeNodeId)
                ? await updateNodeStatus(inherit_node, updatedEdges, subjectNodeId.id, authorizeNodeId.id)
                : inherit_node;

            const updatedNodes = await handleValidateData(updatedStatusNodes, updatedEdges);
            handleValidationFile(updatedNodes, updatedEdges); // อัปเดต validation ของไฟล์ตาม error ของ node
            setNodes(updatedNodes);
        },
        [dispatch, edges, nodes, getEdge, getNode, setEdges, setNodes]
    );

    const handleValidationFile = (nodes, edges) => {
        if (!regulatoryFile) return;

        const updatedFile = {
            ...regulatoryFile,
            diagram: {
                nodes,
                edges: edges
            }
        }

        const result = checkDiagramError(updatedFile);
        if (result?.validation && regulatoryFile.regulatory_diagram_id) {
            const validationResult = {
                regulatory_diagram_id: regulatoryFile.regulatory_diagram_id,
                validation: result?.validation || []
            };
            dispatch(setDrgValidation(validationResult));
        } else {
            dispatch(setDrgValidation(null));
        }
    }

    const handleConfirmNextFile = async () => {
        if (nextFile && (regulatoryFile?.regulatory_diagram_id !== nextFile)) {
            try {
                handleConfirm();
                const response = await fetch(`${process.env.REACT_APP_API_URL}/regulatory-diagram/get-regulatory/${nextFile}`);
                const result = await response.json();
                const normalizedResult = toRegulatoryDomainFile(result);
                dispatch(setCurrentDRG(normalizedResult));
                localStorage.setItem('regulatory-file', JSON.stringify(normalizedResult));
                dispatch(setOpenDrawerRight(false));
            } catch (error) {
                console.error("Error reverting to previous file:", error);
                dispatch(setSnackbarMessage({
                    open: true,
                    message: "Error reverting to previous file.",
                    status: "error",
                }));
            }
        }
    };

    const handleCancelNextFile = async () => {
        if (nextFile && (regulatoryFile?.regulatory_diagram_id !== nextFile)) {
            try {
                const response = await fetch(`${process.env.REACT_APP_API_URL}/regulatory-diagram/get-regulatory/${nextFile}`);
                const result = await response.json();
                const normalizedResult = toRegulatoryDomainFile(result);
                dispatch(setCurrentDRG(normalizedResult));
                localStorage.setItem('regulatory-file', JSON.stringify(normalizedResult));
                setOpenNotificationBeforeChangeFile(false);
            } catch (error) {
                console.error("Error reverting to previous file:", error);
                dispatch(setSnackbarMessage({
                    open: true,
                    message: "Error reverting to previous file.",
                    status: "error",
                }));
            }
        }
    };

    useEffect(() => {
        if (openSimulatorDRG) {
            const currentRegulatoryFile = regulatoryFileRef.current;
            if (!currentRegulatoryFile) return;

            clearActiveHandles();

            const updatedFile = {
                ...currentRegulatoryFile,
                diagram: {
                    nodes: nodesRef.current,
                    edges: edgesRef.current
                }
            }
            // console.log("Check diagram error before open Simulator:", updatedFile);

            const result = checkDiagramError(updatedFile);
            // console.log("Check diagram error before open Simulator:", result);
            if (result?.validation.length > 0) {
                dispatch(setOpenSimulatorDRG(false));
                dispatch(setSnackbarMessage({
                    open: true,
                    message: "Cannot open Simulator because the diagram has errors.",
                    status: "error",
                }));
            } else {
                handleConfirmRef.current?.();
                handleGetSimulatorProfiles(project, profile, subjectSchema, dispatch);
            }
        }
        if (!openSimulatorDRG) {
            // console.log("Reset edge styles when closing Simulator");
            dispatch(setSelectedStatement(null));
        };
    }, [openSimulatorDRG, clearActiveHandles, dispatch, project, profile, subjectSchema]); // เช็ก error ตอนเปิด Simulator

    const applyNodeHighlight = useCallback((isHighlightActive, activeNodeIds = []) => {
        setNodes((currentNodes) => currentNodes.map((node) => {
            if (!isHighlightActive) {
                const { showHandle, highlighted, ...restData } = node.data || {};
                return {
                    ...node,
                    selected: false,
                    data: {
                        ...restData,
                        showHandle: false,
                        highlighted: undefined
                    }
                };
            }
        }));
    }, [setNodes]);

    const applyEdgeCondition = useCallback((isConditionActive, activeEdgeIds = [], edgesToProcess) => {
        const targetEdges = edgesToProcess || edgesRef.current || [];

        return targetEdges.map((edge) => {
            // --- 3. กรณีไม่ได้ใช้เงื่อนไขแล้ว (ยกเลิกโหมดนี้) ---
            if (!isConditionActive) {
                const { filter, opacity, zIndex, stroke, ...restStyle } = edge.style || {};
                return {
                    ...edge,
                    style: {
                        ...restStyle,
                        stroke: 'gray',
                        strokeWidth: 1,
                        opacity: 1,
                        filter: undefined,
                        zIndex: undefined,
                    },
                };
            }

            const isConditionTrue = activeEdgeIds.includes(edge.id);

            if (isConditionTrue) {
                // --- 1. กรณีเงื่อนไขเป็น True (สีน้ำเงิน + มีเงา) ---
                return {
                    ...edge,
                    style: {
                        ...edge.style,
                        stroke: '#1976d2',
                        strokeWidth: 1.5,
                        filter: 'drop-shadow(0px 0px 4px rgba(37, 99, 235, 0.8))', // เพิ่มเงาสีน้ำเงินเรืองแสง
                        opacity: 1,
                        zIndex: 1000
                    }
                };
            } else {
                // --- 2. กรณีเงื่อนไขเป็น False (เส้นจางลง) ---
                return {
                    ...edge,
                    style: {
                        ...edge.style,
                        stroke: '#b1b1b7', // (ตัวเลือกเสริม) บังคับกลับไปใช้สีเทา/สีเดิม
                        strokeWidth: 1,
                        filter: 'none',    // เอาเงาออก (เผื่อเคยมี)
                        opacity: 0.6,
                        zIndex: 0
                    }
                };
            }
        });
    }, []);

    const highlightStatementPaths = useCallback((summarizedData, selectedStatement) => {
        const currentNodes = nodesRef.current || [];
        const currentEdges = edgesRef.current || [];

        let highlightedNodeIds;
        let highlightedEdgeIds;

        if (selectedStatement === 'All') {
            highlightedNodeIds = new Set(summarizedData.flatMap(item => item.path.nodes));
            highlightedEdgeIds = new Set(summarizedData.flatMap(item => item.path.edges));
        } else {
            const targetData = summarizedData.find(item => item.statement === selectedStatement);
            highlightedNodeIds = new Set(targetData ? targetData.path.nodes : []);
            highlightedEdgeIds = new Set(targetData ? targetData.path.edges : []);
        }

        const updatedNodes = currentNodes.map(node => ({
            ...node,
            selected: highlightedNodeIds.has(node.id),
            data: {
                ...node.data,
                highlighted: highlightedNodeIds.has(node.id) ? "highlighted" : "unhighlighted", // เพิ่มสถานะ highlighted สำหรับ node ที่ตรงกับ selectedStatement
            }
        }));

        const edgeIdsToHighlight = currentEdges.map(edge => {
            if (highlightedEdgeIds.has(edge.id)) {
                return edge.id;
            }
        });

        const updatedEdges = applyEdgeCondition(true, edgeIdsToHighlight, currentEdges);
        setEdges(updatedEdges);
        setNodes(updatedNodes);
    }, [applyEdgeCondition, setEdges, setNodes]);

    useEffect(() => {
        // console.log("Applying highlight for statement:", statementHighlightPaths);
        // console.log("Selected statement:", selectedStatement);
        if (selectedStatement && statementHighlightPaths.length > 0) {
            highlightStatementPaths(statementHighlightPaths, selectedStatement);
        } else if (selectedStatement === null && statementHighlightPaths.length > 0) {
            highlightStatementPaths(statementHighlightPaths, 'All');
        } else {
            const resetEdges = applyEdgeCondition(false, [], edgesRef.current);
            setEdges(resetEdges);
            applyNodeHighlight(false);
        }
    }, [selectedStatement, statementHighlightPaths, applyEdgeCondition, applyNodeHighlight, highlightStatementPaths, setEdges])

    const highlightPreviewPaths = (nodes, edges, summarizedData, targetId) => {
        const highlightedNodeIds = new Set(summarizedData.nodes.map(n => n.id));
        const highlightedEdgeIds = new Set(summarizedData.edges.map(e => e.id));

        const updatedNodes = nodes.map(node => ({
            ...node,
            selected: highlightedNodeIds.has(node.id),
            data: {
                ...node.data,
                highlighted: highlightedNodeIds.has(node.id) ? "highlighted" : "unhighlighted", // เพิ่มสถานะ highlighted สำหรับ node ที่ตรงกับ selectedStatement
            }
        }));

        const edgeIdsToHighlight = edges.map(edge => {
            if (highlightedEdgeIds.has(edge.id)) {
                return edge.id;
            }
        });

        const updatedEdges = applyEdgeCondition(true, edgeIdsToHighlight, edges);
        setEdges(updatedEdges);
        setNodes(updatedNodes);

        // dispatch(setPreviewCurrentRedux({ nodes: updatedNodes, edges: updatedEdges }));
        handleDiagramPreviewChange({ nodes: updatedNodes, edges: updatedEdges });

        // const updatedNodes = nodes.map(node => {
        //     const isHighlighted = highlightedNodeIds.has(node.id);
        //     return {
        //         ...node,
        //         selected: isHighlighted,
        //         data: {
        //             ...node.data,
        //             showHandle: (isHighlighted && node.id === targetId && !openPreviewDRGRef.current) ? true : false
        //         }
        //     };
        // });

        // const updatedEdges = edges.map(edge => {
        //     const isHighlighted = highlightedEdgeIds.has(edge.id);
        //     return {
        //         ...edge,
        //         selected: isHighlighted,
        //         style: { ...(edge.style || {}), strokeWidth: isHighlighted ? 1 : 1 }
        //     };
        // });

        // setNodes(updatedNodes);
        // setEdges(updatedEdges);

    };

    const handleHighlightPreview = (nodeId) => {
        const resultPaths = handleClickFindTargetPath(nodeId, nodesRef.current, edgesRef.current);
        const pathsWithEdges = findEdgesForPaths(resultPaths, edgesRef.current);
        const summarizedData = summarizeNodesAndEdges(pathsWithEdges, nodesRef.current, edgesRef.current);
        highlightPreviewPaths(nodesRef.current, edgesRef.current, summarizedData, nodeId);
    };

    return (
        <div className='h-[calc(100vh-48px)] relative'>
            {regulatoryFile &&
                <>
                    <ReactFlow
                        nodes={nodesWithUpdatedData}
                        edges={edgesWithUpdatedData}
                        nodesDraggable={!openPreviewDRG && !openSimulatorDRG}
                        nodesConnectable={!openPreviewDRG && !openSimulatorDRG}
                        elementsSelectable={!openPreviewDRG && !openSimulatorDRG}
                        edgesReconnectable={!openPreviewDRG && !openSimulatorDRG}
                        panOnDrag
                        nodeTypes={nodeTypes}
                        onNodesChange={onNodesChange}
                        onEdgesChange={onEdgesChange}
                        onConnectStart={onConnectStart}
                        onReconnectStart={onReconnectStart}
                        onConnectEnd={onConnectEnd}
                        onConnect={onConnect}
                        onReconnect={onReconnect}
                        onNodeClick={onNodeClick}
                        onPaneClick={onPaneClick}
                        onEdgeClick={onEdgeClick}
                        onDrop={onDrop}
                        onDragOver={onDragOver}
                        onMoveEnd={saveViewportState}
                        onNodeDoubleClick={onNodeDoubleClick}
                        onEdgeMouseEnter={onEdgeMouseEnter}
                        onEdgeMouseLeave={onEdgeMouseLeave}
                        // onDelete={onDelete}
                        onNodeDragStart={onNodeDragStart}
                        onSelectionStart={onSelectionStart}
                        onSelectionChange={onSelectionChange}
                        // deleteKeyCode={["Delete", "Backspace"]}
                        deleteKeyCode={null}
                        onNodeDrag={onNodeDrag}
                        onNodeDragStop={onNodeDragStop}
                        defaultEdgeOptions={defaultEdgeOptions}
                    // panOnDrag={false}
                    // selectionOnDrag={true}
                    // fitView
                    >
                        <Background style={{ backgroundColor: "#fff" }} />
                    </ReactFlow>
                    <Drawer
                        open={openRightSide && !openPreviewDRG && !openSimulatorDRG}
                        anchor="right"
                        variant="persistent"
                        sx={{
                            width: drawerWidth,
                            // flexShrink: 0,
                            zIndex: 1,
                            [`& .MuiDrawer-paper`]: { width: drawerWidth, boxSizing: 'border-box', marginTop: "48px" },
                        }}
                    >
                        <RegulatoryRightSide
                            selectedNode={selectedNode}
                            nodes={nodes}
                            setNodes={setNodes}
                            edges={edges}
                            open={openProperties}
                            setOpen={setOpenProperties}
                            setOpenSelectResource={setOpenSelectResource}
                            setOpenAttributeSelection={setOpenAttributeSelection}
                            setUpdateProperties={setUpdateProperties}
                        />
                    </Drawer>
                    {openSimulatorDRG && openRightSide && openProperties && !openPreviewDRG && (
                        <Box
                            sx={{
                                position: 'fixed',
                                top: 'calc(48px + 16px)',
                                bottom: '16px',
                                right: '16px',
                                width: drawerWidth,
                                maxWidth: 'calc(100vw - 32px)',
                                zIndex: (theme) => theme.zIndex.modal + 10,
                            }}
                        >
                            <Card
                                sx={{
                                    position: 'relative',
                                    height: '100%',
                                    overflow: 'hidden',
                                    boxShadow: 6,
                                }}
                            >
                                <IconButton
                                    aria-label="close regulatory properties"
                                    onClick={handleCloseSimulatorFloatingPanel}
                                    size="small"
                                    sx={{
                                        position: 'absolute',
                                        top: 8,
                                        right: 8,
                                        zIndex: 1,
                                        backgroundColor: 'rgba(255, 255, 255, 0.95)',
                                        '&:hover': {
                                            backgroundColor: 'rgba(245, 245, 245, 1)',
                                        },
                                    }}
                                >
                                    <CloseIcon fontSize="small" />
                                </IconButton>
                                <Box
                                    sx={{
                                        height: '100%',
                                        overflowY: 'auto',
                                    }}
                                >
                                    <RegulatoryRightSide
                                        selectedNode={selectedNode}
                                        nodes={nodes}
                                        setNodes={setNodes}
                                        edges={edges}
                                        open={openProperties}
                                        setOpen={setOpenProperties}
                                        setOpenSelectResource={setOpenSelectResource}
                                        setOpenAttributeSelection={setOpenAttributeSelection}
                                        setUpdateProperties={setUpdateProperties}
                                    />
                                </Box>
                            </Card>
                        </Box>
                    )}
                    <SelectResourceDialog
                        open={openSelectResource}
                        setOpen={setOpenSelectResource}
                        selectedNode={selectedNode}
                        setNodes={setNodes}
                        setUpdateProperties={setUpdateProperties}
                    />
                    <AttributeSelectionNew
                        open={openAttributeSelection}
                        setOpen={setOpenAttributeSelection}
                        selectedNode={selectedNode}
                        nodes={nodes}
                        setNodes={setNodes}
                        edges={edges}
                        setUpdateProperties={setUpdateProperties}
                    />
                    <Backdrop
                        sx={{
                            color: '#fff',
                            zIndex: 1,
                            backgroundColor: 'rgba(220, 220, 220, 1)',
                            position: 'absolute'
                        }}
                        open={loading}
                        onClick={() => setLoading(false)}
                    >
                        <CircularProgress />
                    </Backdrop>

                    <DialogReuse
                        open={openResourceUpdate}
                        // onClose={handleResourceUpdateClose}
                        fullWidth={true}
                        maxWidth={"sm"}
                        title={"Update Resource"}
                        content={
                            <div className='py-2 overflow-auto'>
                                {resourceUpdateDetail?.added.length > 0 &&
                                    <Box mb={1}>
                                        <Typography component={'span'} variant="body1" gutterBottom>Added data include:
                                            {resourceUpdateDetail?.added.map((item, index) => {
                                                return (
                                                    <div key={index} className='my-1'>
                                                        <Typography component={'span'} variant="body2" color="success">- {item.name}</Typography>
                                                    </div>
                                                )
                                            })}
                                        </Typography>
                                    </Box>
                                }
                                {resourceUpdateDetail?.removed.length > 0 &&
                                    <Box>
                                        <Typography component={'span'} variant="body1" gutterBottom>Removed data include:
                                            {resourceUpdateDetail?.removed.map((item, index) => {
                                                return (
                                                    <div key={index} className='my-1'>
                                                        <Typography component={'span'} variant="body2" color="error">- {item.name}</Typography>
                                                    </div>
                                                )
                                            })}
                                        </Typography>
                                    </Box>
                                }
                                {resourceUpdateDetail?.modified.length > 0 &&
                                    <Box>
                                        <Typography component={'span'} variant="body1">Data type change:
                                            {resourceUpdateDetail?.modified.map((item, index) => {
                                                return (
                                                    <div key={index}>
                                                        <Typography component={'span'} variant="body2">- {item.name}: {item.oldDataType} {"→"} {item.newDataType}</Typography>
                                                    </div>
                                                )
                                            })}
                                        </Typography>
                                    </Box>
                                }
                            </div>
                        }
                        actions={
                            <LoadingButton variant="contained" onClick={() => handleResourceUpdate()}>OK</LoadingButton>
                        }
                    />
                    <DialogReuse
                        open={openNotificationDpe}
                        onClose={handleNotificationDPEClose}
                        fullWidth={true}
                        maxWidth={"sm"}
                        title={"Update DPE"}
                        content={
                            <div className='py-4'>
                                <Box mb={1}>
                                    <Typography component={'span'} variant="body1" gutterBottom>{notificationDpeDetail}</Typography>
                                </Box>
                            </div>
                        }
                        actions={
                            <LoadingButton variant="contained" onClick={() => {
                                if (notificationDpeEvent === 'dpe_id') {
                                    handleSettingResource()
                                } else {
                                    handleNotificationDPEClose() // รอข้อมูล DPE ทดสอบ เพื่อนำไปเช็ก event ตามที่ note ไว้
                                }
                            }}>Next</LoadingButton>
                        }
                    />
                    <ConfirmDialogReuse
                        open={openNotificationBeforeChangeFile}
                        maxWidth={"sm"}
                        content={
                            <div>
                                <Typography variant="subtitle1">Changes detected. Please save.</Typography>
                            </div>
                        }
                        actions={
                            <div className='grid grid-cols-2 gap-2'>
                                <LoadingButton
                                    variant="contained"
                                    loading={loadingConfirmDialog}
                                    onClick={() => handleConfirmNextFile()}
                                >
                                    Confirm
                                </LoadingButton>
                                <Button
                                    disabled={loadingConfirmDialog}
                                    variant="outlined"
                                    onClick={() => handleCancelNextFile()}
                                >
                                    Cancel
                                </Button>
                            </div>
                        }
                    />
                    {/* <ConfirmDialogReuse
                        open={openNotificationBeforePreview}
                        maxWidth={"sm"}
                        content={
                            <div>
                                <Typography variant="subtitle1">Changes detected. Please save before preview.</Typography>
                            </div>
                        }
                        actions={
                            <div className='grid grid-cols-2 gap-2'>
                                <LoadingButton
                                    variant="contained"
                                    loading={loadingConfirmDialog}
                                    onClick={() => handleConfirm()}
                                >
                                    Confirm
                                </LoadingButton>
                                <Button
                                    disabled={loadingConfirmDialog}
                                    variant="outlined"
                                    onClick={() => setOpenNotificationBeforePreview(false)}
                                >
                                    Cancel
                                </Button>
                            </div>
                        }
                    /> */}
                </>
            }
        </div>
    )
}

export default RegulatoryDiagram